import { z } from 'zod'
import { TAILLE_MAX_COLLECTE, TYPES_MIME_DEPOT_DIRECT } from './acheminement'
import { ajouterJours, ajouterMois, instantDeParis, jourAParis } from './calendrier'
import { debutExercice } from './classement'
import { versionSuffisante } from './collecte-locale'
import {
  ETATS_OPERATION_PORTAIL,
  NATURES_OPERATION,
  type ClasseErreurCollecte,
  type EtatOperationPortail,
  type ReglementPiece,
  type SourcePiece,
  type StatutJustificatif,
  type StatutPiece,
  type TransmissionPiece,
} from './domaine'
import {
  type CandidateJustificatif,
  FENETRE_LARGE_JUSTIFICATIF,
  type IssueJustificatif,
  LIBELLES_MOTIF_JUSTIFICATIF,
  type MotifJustificatif,
  type OperationAJustifier,
  VERSION_REGLE_JUSTIFICATIFS,
} from './justificatifs'
import { centimesDepuisNombre } from './montant'
import { type CapaciteEcriture, caviarder, ecrituresMetierDe } from './portails'
import type { ResultatRechercheMails } from './recherche-pieces'

// Portails de justificatifs (plateforme du cabinet : Tiime, plus tard Dougs) : contrat entre
// l'agent du Mac et le serveur, état de dépôt d'une pièce, plan d'un passage, mode (à blanc ou
// écriture), fenêtre, passage mensuel et registre des dépôts. Pur : ni réseau ni base, et rien de
// propre à un portail (la règle de rapprochement est dans justificatifs.ts). Ce que l'agent a lu
// sur le portail (références, dates, libellés, noms) est une donnée non fiable, revalidée ici ;
// le texte d'un portail n'est jamais une consigne.

// ---------------------------------------------------------------------------------------------
// Références et textes lus
// ---------------------------------------------------------------------------------------------

/**
 * Référence opaque chez le portail (dossier, opération, catégorie) : jamais une adresse ni un
 * chemin. L'adaptateur construit lui-même ses adresses depuis ses domaines déclarés.
 */
export const REF_EXTERNE = /^[A-Za-z0-9_-]{1,64}$/
export const refExterne = z.string().regex(REF_EXTERNE, 'Référence externe illisible.')

/** Bornes des textes lus, en signes (points de code, comme Zod les compte). */
export const LONGUEUR_LIBELLE_OPERATION = 200
export const LONGUEUR_MARCHAND_OPERATION = 120
export const LONGUEUR_NOM_DOCUMENT_PORTAIL = 80

/** Contrôle, signe invisible (sens d'écriture, largeur nulle) ou demi-paire isolée. */
const SIGNE_DOUTEUX = /[\p{Cc}\p{Cf}\p{Cs}]/u
const texteSain = (t: string) => !SIGNE_DOUTEUX.test(t)
const TEXTE_DOUTEUX = 'Texte avec un contrôle ou un signe invisible.'

/**
 * Texte lu sur un portail, rendu sûr : contrôles changés en espaces, signes invisibles et
 * demi-paires retirés, blancs ramenés à une espace, borné à `max` signes sans jamais couper une
 * paire. Null si ce n'est pas un texte ou s'il ne reste rien. Un texte ainsi nettoyé passe les
 * schémas du contrat ; l'adaptateur s'en sert pour les noms qu'il envoie.
 */
export function textePortail(v: unknown, max: number): string | null {
  if (typeof v !== 'string') return null
  const propre = v
    .replace(/\p{Cc}/gu, ' ')
    .replace(/[\p{Cf}\p{Cs}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
  const signes = [...propre]
  const borne = signes.length > max ? signes.slice(0, max).join('').trimEnd() : propre
  return borne || null
}

const sansDoublon = (refs: readonly string[]) => new Set(refs).size === refs.length

// ---------------------------------------------------------------------------------------------
// Contrat entre l'agent et le serveur (schémas stricts)
// ---------------------------------------------------------------------------------------------

/** Dossiers que voit le compte du titulaire chez le portail ; le serveur choisit par le SIREN. */
export const MAX_DOSSIERS_PORTAIL = 50

export const dossiersPortail = z
  .object({
    dossiers: z
      .array(
        z
          .object({
            ref: refExterne,
            siret: z
              .string()
              .regex(/^\d{14}$/, 'SIRET : 14 chiffres.')
              .nullable(),
            /** Le portail ne donne que le SIREN (Dougs) ; avec un SIRET, il doit en être le début. */
            siren: z
              .string()
              .regex(/^\d{9}$/, 'SIREN : 9 chiffres.')
              .nullable()
              .optional(),
          })
          .strict()
          .refine((d) => !d.siren || !d.siret || d.siret.startsWith(d.siren), {
            message: 'SIREN et SIRET discordants.',
          }),
      )
      .min(1)
      .max(MAX_DOSSIERS_PORTAIL),
  })
  .strict()
  .refine((d) => sansDoublon(d.dossiers.map((x) => x.ref)), {
    message: 'Dossier en double.',
    path: ['dossiers'],
  })
export type DossiersPortail = z.infer<typeof dossiersPortail>

/** SIREN d'un dossier lu : celui que donne le portail, sinon le début de son SIRET. */
export function sirenDuDossier(d: DossiersPortail['dossiers'][number]): string | null {
  return d.siren ?? (d.siret === null ? null : d.siret.slice(0, 9))
}

/**
 * Refus du choix du dossier : aucun ou plusieurs dossiers portent le SIREN de l'entité, le
 * dossier n'est plus celui épinglé au premier passage, l'entité ne s'y prête pas (sans SIREN,
 * privée ou archivée).
 */
export const REFUS_DOSSIER_JUSTIFICATIFS = ['aucun', 'plusieurs', 'change', 'entite'] as const
export type RefusDossierJustificatifs = (typeof REFUS_DOSSIER_JUSTIFICATIFS)[number]

/** Au-delà, le compte de justificatifs posés chez la source est ramené à cette borne. */
export const MAX_JUSTIFICATIFS_SOURCE = 1000

/** Une opération lue chez le portail, telle que l'agent l'envoie au serveur. */
export const operationPortail = z
  .object({
    ref: refExterne,
    /** Jour de l'opération à Paris (AAAA-MM-JJ, jour réel). */
    date: z.iso.date(),
    /** Signé, en centimes : négatif pour un débit. */
    montantCts: z.int(),
    devise: z.string().regex(/^[A-Z]{3}$/, 'Devise : code ISO en majuscules.'),
    libelle: z.string().max(LONGUEUR_LIBELLE_OPERATION).refine(texteSain, TEXTE_DOUTEUX),
    marchand: z
      .string()
      .min(1)
      .max(LONGUEUR_MARCHAND_OPERATION)
      .refine(texteSain, TEXTE_DOUTEUX)
      .nullable(),
    nature: z.enum(NATURES_OPERATION),
    /** Justificatifs déjà posés chez la source, imputations comprises. */
    justificatifs: z.int().min(0).max(MAX_JUSTIFICATIFS_SOURCE),
    ventilee: z.boolean(),
    provisoire: z.boolean(),
    /** Validée chez la source par un humain (Dougs) ; absent quand la source ne le dit pas. */
    validee: z.boolean().optional(),
  })
  .strict()
export type OperationPortail = z.infer<typeof operationPortail>

/** Opérations par lot ; vingt lots au plus par passage. */
export const MAX_OPERATIONS_PAR_LOT = 500
export const MAX_LOTS_OPERATIONS = 20
/** Lignes lues au plus par passage. */
export const MAX_LIGNES_LUES = MAX_OPERATIONS_PAR_LOT * MAX_LOTS_OPERATIONS
/** Justificatifs chez le portail sans liaison : leur nombre, et quelques exemples à montrer. */
export const MAX_EXEMPLES_SANS_LIAISON = 20
export const MAX_DOCUMENTS_SANS_LIAISON = 100_000

/** Relecture d'une opération par l'adaptateur (sa sonde) : confirmée, impossible, non tentée. */
export const RELECTURES_JUSTIFICATIFS = ['ok', 'indisponible'] as const
export type RelectureJustificatifs = (typeof RELECTURES_JUSTIFICATIFS)[number] | null

const champsSansLiaison = {
  nombre: z.int().min(0).max(MAX_DOCUMENTS_SANS_LIAISON),
  exemples: z
    .array(
      z
        .object({
          date: z.iso.date(),
          nom: z
            .string()
            .min(1)
            .max(LONGUEUR_NOM_DOCUMENT_PORTAIL)
            .refine(texteSain, TEXTE_DOUTEUX),
        })
        .strict(),
    )
    .max(MAX_EXEMPLES_SANS_LIAISON),
}
const exemplesBornes = {
  message: 'Plus d’exemples que de documents.',
  path: ['exemples'],
}

/**
 * Justificatifs déjà chez le portail, reliés à aucune opération : listés à part au bilan. Ceux de
 * l'exercice examiné (arrivés chez le portail depuis `exerciceDu`, rendu à la prise), et à part
 * ceux d'avant (`anterieurs`, l'historique). Un agent qui ne reçoit pas `exerciceDu` (avant 0.5.1)
 * les compte tous dans `nombre`, sans `anterieurs`.
 */
export const documentsSansLiaison = z
  .object({
    ...champsSansLiaison,
    anterieurs: z
      .object(champsSansLiaison)
      .strict()
      .refine((d) => d.exemples.length <= d.nombre, exemplesBornes)
      .optional(),
  })
  .strict()
  .refine((d) => d.exemples.length <= d.nombre, exemplesBornes)
export type DocumentsSansLiaison = z.infer<typeof documentsSansLiaison>

/**
 * Fin de la lecture, portée par le dernier lot : lecture complète (début de la période atteint),
 * lignes lues et illisibles, catégorie de dépôt, relecture, justificatifs sans liaison.
 */
export const finLecture = z
  .object({
    complete: z.boolean(),
    lues: z.int().min(0).max(MAX_LIGNES_LUES),
    illisibles: z.int().min(0).max(MAX_LIGNES_LUES),
    categorie: refExterne.nullable(),
    relecture: z.enum(RELECTURES_JUSTIFICATIFS).nullable(),
    documentsSansLiaison: documentsSansLiaison.nullable(),
  })
  .strict()
  .refine((f) => f.illisibles <= f.lues, {
    message: 'Plus de lignes illisibles que de lues.',
    path: ['illisibles'],
  })
export type FinLecture = z.infer<typeof finLecture>

/** Un lot d'opérations lues ; le dernier porte `fin`. Un lot plein tient sous 1 Mo. */
export const lotOperationsPortail = z
  .object({
    dossier: refExterne,
    rang: z
      .int()
      .min(0)
      .max(MAX_LOTS_OPERATIONS - 1),
    operations: z.array(operationPortail).max(MAX_OPERATIONS_PAR_LOT),
    fin: finLecture.optional(),
  })
  .strict()
  .refine((l) => sansDoublon(l.operations.map((o) => o.ref)), {
    message: 'Opération en double dans le lot.',
    path: ['operations'],
  })
export type LotOperationsPortail = z.infer<typeof lotOperationsPortail>

/**
 * Les lots d'un passage : par 500, dans l'ordre, au moins un (une lecture vide envoie sa fin), la
 * fin sur le dernier. Au-delà de vingt lots, erreur : l'adaptateur borne sa lecture avant.
 */
export function lotsOperationsPortail(
  dossier: string,
  operations: readonly OperationPortail[],
  fin: FinLecture,
): LotOperationsPortail[] {
  const nombre = Math.max(1, Math.ceil(operations.length / MAX_OPERATIONS_PAR_LOT))
  if (nombre > MAX_LOTS_OPERATIONS)
    throw new RangeError(
      `Trop d’opérations pour un passage : ${operations.length} (${MAX_LIGNES_LUES} au plus).`,
    )
  return Array.from({ length: nombre }, (_, rang) => {
    const debut = rang * MAX_OPERATIONS_PAR_LOT
    const lot: LotOperationsPortail = {
      dossier,
      rang,
      operations: operations.slice(debut, debut + MAX_OPERATIONS_PAR_LOT),
    }
    if (rang === nombre - 1) lot.fin = fin
    return lot
  })
}

/**
 * Ce qu'un humain décide sur une opération, dans l'appli (jamais via l'assistant) : écarter ou
 * rouvrir (qui efface aussi un choix) ; reconnaître ou contester une pièce déjà chez le cabinet ;
 * choisir la pièce d'une opération à trancher (« C'est celle-ci », décision de Pierre-Marie du
 * 13/10/2026 : le choix vaut décision, l'agent du Mac la relie au passage suivant).
 */
export const decisionJustificatif = z.union([
  z.object({ operationId: z.uuid(), choix: z.enum(['ecarter', 'rouvrir']) }).strict(),
  z
    .object({
      operationId: z.uuid(),
      choix: z.enum(['reconnaitre', 'contester', 'choisir']),
      pieceId: z.uuid(),
    })
    .strict(),
])
export type DecisionJustificatif = z.infer<typeof decisionJustificatif>

/** Le titulaire valide le dernier passage à blanc de la connexion. */
export const validationPassage = z.object({ collecteId: z.uuid() }).strict()
export type ValidationPassage = z.infer<typeof validationPassage>

// ---------------------------------------------------------------------------------------------
// Normalisation des opérations lues par l'adaptateur
// ---------------------------------------------------------------------------------------------

const JOUR = /^(\d{4})-(\d{2})-(\d{2})$/
/** Instant ISO 8601 : heure et minutes, secondes et fraction facultatives, fuseau facultatif. */
const INSTANT =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(Z|([+-])(\d{2}):?(\d{2}))?$/i
/** Décalage horaire le plus grand qui existe. */
const DECALAGE_MAX_HEURES = 14

function jourReel(j: string): boolean {
  const m = JOUR.exec(j)
  if (!m) return false
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return new Date(t).toISOString().slice(0, 10) === j
}

/**
 * Jour (Paris) d'une date lue : un jour réel tel quel ; un instant avec fuseau ramené à Paris
 * (`jourAParis`) ; un instant sans fuseau est à l'heure locale de la source, il garde son jour.
 * Calcul sans `Date.parse`, qui reporte un 30 février au 2 mars. Sinon null.
 */
function jourLu(v: unknown): string | null {
  if (typeof v !== 'string') return null
  if (JOUR.test(v)) return jourReel(v) ? v : null
  const m = INSTANT.exec(v)
  if (!m) return null
  const [, jour, heure, minute, seconde, fuseau, signe, fh, fm] = m
  if (!jourReel(jour!) || Number(heure) > 23 || Number(minute) > 59 || Number(seconde ?? 0) > 59)
    return null
  if (fuseau === undefined) return jour!
  let decalageMinutes = 0
  if (fuseau.toUpperCase() !== 'Z') {
    if (Number(fh) > DECALAGE_MAX_HEURES || Number(fm) > 59) return null
    decalageMinutes = (signe === '-' ? -1 : 1) * (Number(fh) * 60 + Number(fm))
  }
  const [a, mo, j] = jour!.split('-').map(Number)
  const utc =
    Date.UTC(a!, mo! - 1, j!, Number(heure), Number(minute), Number(seconde ?? 0)) -
    decalageMinutes * 60_000
  return jourAParis(new Date(utc))
}

/**
 * Une ligne lue, revalidée ; null si elle est illisible. Sont exigés : une référence externe, un
 * jour, un montant, une devise, le compte des justificatifs et les deux drapeaux (un compte ou un
 * drapeau illisible ne vaut jamais « sans justificatif » ni « réalisée »). Sont seulement oubliés :
 * le libellé (vide), le marchand (nul), la nature (autre).
 */
function lireOperation(v: unknown): OperationPortail | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null
  const l = v as Record<string, unknown>
  const date = jourLu(l.date)
  const montantCts =
    typeof l.montant === 'number' || typeof l.montant === 'string'
      ? centimesDepuisNombre(l.montant)
      : null
  const justificatifs =
    typeof l.justificatifs === 'number' &&
    Number.isSafeInteger(l.justificatifs) &&
    l.justificatifs >= 0
      ? Math.min(l.justificatifs, MAX_JUSTIFICATIFS_SOURCE)
      : null
  if (date === null || montantCts === null || justificatifs === null) return null
  // Facultatif, mais jamais deviné : présent et illisible, la ligne l'est aussi.
  if (l.validee !== undefined && typeof l.validee !== 'boolean') return null
  const r = operationPortail.safeParse({
    ref: l.ref,
    date,
    montantCts,
    devise: typeof l.devise === 'string' ? l.devise.trim().toUpperCase() : null,
    libelle: textePortail(l.libelle, LONGUEUR_LIBELLE_OPERATION) ?? '',
    marchand: textePortail(l.marchand, LONGUEUR_MARCHAND_OPERATION),
    nature: (NATURES_OPERATION as readonly unknown[]).includes(l.nature) ? l.nature : 'autre',
    justificatifs,
    ventilee: l.ventilee,
    provisoire: l.provisoire,
    ...(l.validee === undefined ? {} : { validee: l.validee }),
  })
  return r.success ? r.data : null
}

export type NormalisationOperations = {
  operations: OperationPortail[]
  /** Lignes écartées : référence, jour, montant, devise, compte ou drapeau illisible. */
  illisibles: number
  /** Lignes d'une référence déjà gardée (page relue après un décalage). */
  doublons: number
  /** Lignes d'un jour lisible hors de la période lue : écartées sans compter, lisibles ou non. */
  horsPeriode: number
}

/** Jour lisible d'une ligne lue hors de la période ; faux pour un jour illisible. */
function horsDeLaPeriode(v: unknown, periode: { du: string; au: string }): boolean {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false
  const jour = jourLu((v as Record<string, unknown>).date)
  return jour !== null && (jour < periode.du || jour > periode.au)
}

/**
 * Les opérations que l'adaptateur a lues (`{ ref, date, montant, devise, libelle, marchand,
 * nature, justificatifs, ventilee, provisoire, validee? }`, `montant` brut, en unités : nombre JSON ou
 * chaîne), revalidées sur le modèle de `normaliserDocuments` : référence revalidée, jour ou
 * instant ramené à Paris, montant par `centimesDepuisNombre`, textes nettoyés et bornés. Une ligne
 * illisible est écartée et comptée ; une référence vue deux fois ne compte qu'une fois, la
 * première ligne lisible. Avec la période lue, une ligne dont le jour se lit hors d'elle est
 * écartée sans compter (le serveur l'ignore, l'unicité ne la voit pas) : une ligne ancienne mal
 * formée ne rend pas la lecture incomplète ; un jour illisible compte toujours.
 */
export function normaliserOperationsPortail(
  lues: readonly unknown[],
  periode?: { du: string; au: string },
): NormalisationOperations {
  const vues = new Set<string>()
  const operations: OperationPortail[] = []
  let illisibles = 0
  let doublons = 0
  let horsPeriode = 0
  for (const brute of lues) {
    if (periode && horsDeLaPeriode(brute, periode)) {
      horsPeriode++
      continue
    }
    const op = lireOperation(brute)
    if (!op) illisibles++
    else if (vues.has(op.ref)) doublons++
    else {
      vues.add(op.ref)
      operations.push(op)
    }
  }
  return { operations, illisibles, doublons, horsPeriode }
}

// ---------------------------------------------------------------------------------------------
// État de dépôt d'une pièce
// ---------------------------------------------------------------------------------------------

/** Lignes vivantes du registre : jamais deux pour une même pièce, toutes connexions confondues. */
export const STATUTS_JUSTIFICATIF_VIVANTS = [
  'reserve',
  'depose',
  'relie',
  'incertain',
  'orphelin',
  'present',
] as const satisfies readonly StatutJustificatif[]

/** Pourquoi une pièce ne se dépose pas seule. */
export const MOTIFS_NON_DEPOSABLE = [
  'depot_en_cours',
  'depot_incertain',
  'avance',
  'payeur_a_indiquer',
  'reglement_a_indiquer',
  'fichier_refuse',
  'copie_du_mail',
  'entite_hors_depot',
  'essai_plus_tard',
  'sans_objet',
] as const
export type MotifNonDeposable = (typeof MOTIFS_NON_DEPOSABLE)[number]

/** La ligne du registre d'une pièce (la vivante, s'il y en a une). */
export type LigneRegistrePiece = {
  /** Identifiant de la ligne : le justificatif. */
  id: string
  statut: StatutJustificatif
  operationRef: string | null
  documentRef: string | null
}

/** Ce que l'état de dépôt lit d'une pièce, de sa transmission et de son entité. */
export type PieceADeposer = {
  /** Ligne vivante du registre pour la pièce, toutes connexions confondues ; sinon null. */
  ligne: LigneRegistrePiece | null
  transmission: TransmissionPiece | null
  source: SourcePiece
  dejaChezCabinet: boolean
  reglement: ReglementPiece | null
  /** Facture d'un portail d'achats : « qui a payé » attend un humain. */
  payeurAttendu: boolean
  /** Réservée par un envoi ou un dépôt (`transmise_le` posé). */
  reservee: boolean
  /** Le prochain essai de transmission est permis (jamais tenté, ou délai écoulé). */
  essaiPermis: boolean
  /** L'entité de la pièce est dans le mode de dépôt du fournisseur de la connexion. */
  entiteEnDepot: boolean
  /**
   * PDF rendu depuis le corps d'un mail qui vaut pièce : une copie du mail, pas un fichier émis
   * par l'expéditeur (`pieces.rendue_depuis_mail`).
   */
  rendueDepuisMail: boolean
  /**
   * Preuve de débit (décision de Pierre-Marie du 31/10/2026) : facture d'une autre entité, avancée
   * par celle de la connexion, avec sa ligne à refacturer de tout son montant. Elle se dépose chez
   * le cabinet de qui a payé, annoncée sans TVA (`nomPreuveDeDebit`) ; sa transmission reste
   * « sans objet », une avance n'allant jamais seule au cabinet de sa charge.
   */
  preuveDeDebit?: boolean
}

/**
 * Dépôt direct d'une copie du corps d'un mail qui vaut pièce. Vrai depuis la décision de
 * Pierre-Marie du 13/10/2026 : elle se dépose comme les autres, son PDF tenant lieu de fichier
 * d'origine (CLAUDE.md, « le fichier original d'une facture d'achat »). Faux : coupe-circuit, elle
 * se dépose à la main (motif `copie_du_mail`).
 */
export const DEPOT_COPIE_DU_MAIL: boolean = true

export type EtatDepot =
  | { etat: 'deposable' }
  /** Déposée chez le portail pour `operation`, pas encore reliée. */
  | { etat: 'deposee'; justificatifId: string; documentRef: string; operation: string }
  /** Déjà chez le cabinet : reliée, reconnue, orpheline, envoyée ou arrivée par sa plateforme. */
  | { etat: 'presente' }
  | { etat: 'non_deposable'; motif: MotifNonDeposable }

const nonDeposable = (motif: MotifNonDeposable): EtatDepot => ({ etat: 'non_deposable', motif })

/**
 * Où en est une pièce pour le dépôt direct, dans cet ordre :
 * 1. sa ligne vivante au registre (déposée, présente, en cours, incertaine) ;
 * 2. déjà chez le cabinet (transmise, arrivée par sa plateforme) ;
 * 3. un humain d'abord (avance, sauf preuve de débit ; payeur, règlement), envoi incertain, fichier
 *    refusé, copie du corps d'un mail si son dépôt est coupé (`DEPOT_COPIE_DU_MAIL`) ;
 * 4. entité hors du dépôt direct ;
 * 5. à faire, ou preuve de débit sans objet : réservée, essai plus tard, sinon déposable ;
 * 6. sinon sans objet.
 */
export function etatDepotDe(p: PieceADeposer): EtatDepot {
  const ligne = p.ligne
  if (ligne) {
    switch (ligne.statut) {
      case 'depose':
        return ligne.documentRef && ligne.operationRef
          ? {
              etat: 'deposee',
              justificatifId: ligne.id,
              documentRef: ligne.documentRef,
              operation: ligne.operationRef,
            }
          : nonDeposable('depot_incertain')
      case 'relie':
      case 'present':
      case 'orphelin':
        return { etat: 'presente' }
      case 'reserve':
        return nonDeposable('depot_en_cours')
      case 'incertain':
        return nonDeposable('depot_incertain')
      // echec, annule : ligne close, la pièce est comme sans ligne.
    }
  }
  if (p.transmission === 'faite' || p.source === 'plateforme' || p.dejaChezCabinet)
    return { etat: 'presente' }
  if (p.reglement === 'avance' && !p.preuveDeDebit) return nonDeposable('avance')
  if (p.payeurAttendu && p.reglement === null) return nonDeposable('payeur_a_indiquer')
  if (p.transmission === 'a_decider') return nonDeposable('reglement_a_indiquer')
  if (p.transmission === 'incertain') return nonDeposable('depot_incertain')
  if (p.transmission === 'manuelle') return nonDeposable('fichier_refuse')
  if (p.rendueDepuisMail && !DEPOT_COPIE_DU_MAIL) return nonDeposable('copie_du_mail')
  if (!p.entiteEnDepot) return nonDeposable('entite_hors_depot')
  if (p.transmission === 'a_faire' || (p.preuveDeDebit && p.transmission === 'sans_objet')) {
    if (p.reservee) return nonDeposable('depot_en_cours')
    if (!p.essaiPermis) return nonDeposable('essai_plus_tard')
    return { etat: 'deposable' }
  }
  return nonDeposable('sans_objet')
}

// ---------------------------------------------------------------------------------------------
// Plan d'un passage
// ---------------------------------------------------------------------------------------------

/** À blanc : rien n'est écrit chez le portail ; écriture : les sûres y sont déposées et reliées. */
export const MODES_JUSTIFICATIFS = ['a_blanc', 'ecriture'] as const
export type ModeJustificatifs = (typeof MODES_JUSTIFICATIFS)[number]

/** Ce que l'agent fait d'une opération sûre : déposer la pièce puis relier, ou relier seulement. */
export const ACTIONS_JUSTIFICATIF = ['deposer_et_relier', 'relier'] as const
export type ActionJustificatif = (typeof ACTIONS_JUSTIFICATIF)[number]

/** Écritures au plus par passage ; les sûres au-delà attendent le passage suivant. */
export const MAX_ECRITURES_PAR_PASSAGE = 20

/** Motif d'une opération du plan : celui de la règle, ou celui qui retient sa pièce. */
export type MotifPlanJustificatif =
  MotifJustificatif | MotifNonDeposable | 'deja_chez_cabinet' | 'deposee_ailleurs'

export const LIBELLES_MOTIF_DEPOT: Readonly<
  Record<MotifNonDeposable | 'deja_chez_cabinet' | 'deposee_ailleurs', string>
> = {
  depot_en_cours: 'Dépôt en cours',
  depot_incertain: 'Dépôt interrompu : vérifier chez le cabinet s’il est arrivé',
  avance: 'Avancée par une personne selon l’appli : vérifier qui l’a réglée',
  payeur_a_indiquer: 'Qui a payé : à indiquer d’abord',
  reglement_a_indiquer: 'Règlement à indiquer d’abord',
  fichier_refuse: 'Fichier refusé par le dépôt direct : à déposer à la main',
  copie_du_mail: 'Copie du corps d’un mail, pas le fichier de l’expéditeur : à déposer à la main',
  entite_hors_depot: 'L’entité n’est pas en dépôt direct',
  essai_plus_tard: 'Nouvel essai de dépôt plus tard',
  sans_objet: 'Rien à déposer pour cette pièce',
  deja_chez_cabinet: 'Déjà chez le cabinet : la relier à la main',
  deposee_ailleurs: 'Déposée pour une autre opération : vérifier chez le cabinet',
}

export const LIBELLES_MOTIF_PLAN: Readonly<Record<MotifPlanJustificatif, string>> = {
  ...LIBELLES_MOTIF_JUSTIFICATIF,
  ...LIBELLES_MOTIF_DEPOT,
}

/**
 * Une pièce montrée pour une opération : une candidate de la règle, ou la pièce sûre (sans score :
 * la règle ne l'ordonne pas), toujours avec ses signaux et son taux effectif.
 */
export type PieceEnVue = Omit<CandidateJustificatif, 'score'> & { score: number | null }

/** L'état d'une opération après le plan, à écrire sur l'opération lue. */
export type OperationPlanifiee = {
  ref: string
  etat: EtatOperationPortail
  /** Pièce retenue : sûre, reconnue chez la source ou reliée ; sinon null. */
  pieceId: string | null
  /** Ce que ferait (à blanc) ou fera (écriture) le passage pour une sûre. */
  action: ActionJustificatif | null
  /** Candidates (cinq au plus), ou la pièce sûre. */
  candidates: PieceEnVue[]
  motif: MotifPlanJustificatif | null
  /**
   * La sûre vient du choix d'un humain (« C'est celle-ci »), pas de la règle : absent sinon. Le
   * passage la traite comme une sûre (mêmes contrôles, même action).
   */
  choisie?: true
}

/** Une écriture rendue à l'agent ; il relit l'opération (jour, montant, devise) avant d'écrire. */
export type ActionPlanJustificatif = {
  operation: string
  action: ActionJustificatif
  date: string
  montantCts: number
  devise: string
  /** Pour relier : la ligne du registre de la pièce déjà déposée. */
  justificatifId?: string
}

/** Pièce reconnue chez la source, à marquer « déjà chez le cabinet » (N0, à blanc compris). */
export type PieceAMarquer = { pieceId: string; operation: string }

export type ComptesJustificatifs = Record<EtatOperationPortail, number>

/** Une ligne du registre sur une opération (relie : reliée par l'appli ; present : reconnue). */
export type LigneRegistreOperation = { pieceId: string; statut: StatutJustificatif }

export type EntreePlanJustificatifs = {
  /** Issues de la règle (`rapprocherJustificatifs`), par opération (ref), dans leur ordre. */
  issues: ReadonlyMap<string, IssueJustificatif>
  /**
   * Chaque opération des issues : ce qu'il faut pour une action, et ses justificatifs ; ventilée,
   * provisoire ou validée chez la source (absent : non), qui retiennent un choix humain.
   */
  operations: ReadonlyMap<
    string,
    Pick<OperationAJustifier, 'date' | 'montantCts' | 'devise' | 'justificatifs'> &
      Partial<Pick<OperationAJustifier, 'ventilee' | 'provisoire' | 'validee'>>
  >
  /** État de dépôt de chaque pièce en jeu (`etatDepotDe`) ; sans état, jamais ni déposée ni marquée. */
  etatsPieces: ReadonlyMap<string, EtatDepot>
  /** Lignes vivantes du registre, par opération (ref). */
  registre: ReadonlyMap<string, readonly LigneRegistreOperation[]>
  /** Opérations écartées par un humain (ref). */
  ecartees: ReadonlySet<string>
  /**
   * Choix d'un humain (« C'est celle-ci », décision du 13/10/2026) : opération (ref) vers la pièce
   * choisie. Absent : aucun choix.
   */
  choix?: ReadonlyMap<string, string>
  /** Mode figé à la prise ; les actions n'existent qu'en écriture. */
  mode: ModeJustificatifs
  /** Sonde de relecture de l'adaptateur : sans relecture confirmée, aucune action. */
  relecture: RelectureJustificatifs
  /** Plafond demandé, jamais au-delà de `MAX_ECRITURES_PAR_PASSAGE`. */
  maxActions?: number
}

export type PlanJustificatifs = {
  /** Une par issue, dans l'ordre des issues. */
  operations: OperationPlanifiee[]
  marquer: PieceAMarquer[]
  /** Vides à blanc ; triées par jour puis par référence, vingt au plus. */
  actions: ActionPlanJustificatif[]
  comptes: ComptesJustificatifs
}

const INCONNUE: EtatDepot = { etat: 'non_deposable', motif: 'sans_objet' }

const comparer = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

const enVue = (c: CandidateJustificatif): PieceEnVue => ({ ...c, signaux: [...c.signaux] })

/** La pièce sûre d'une issue, sans score (la règle ne l'ordonne pas), avec ses signaux. */
const pieceSure = (issue: Extract<IssueJustificatif, { issue: 'sure' }>): PieceEnVue => ({
  pieceId: issue.pieceId,
  score: null,
  signaux: [...issue.signaux],
  ecartJours: issue.ecartJours,
  rapportChange: issue.rapportChange,
})

/**
 * Le plan d'un passage : l'état de chaque opération, les pièces à marquer et les actions. Pour
 * chaque opération, dans cet ordre :
 * 1. hors fenêtre ;
 * 2. reliée par l'appli (ligne `relie` sur l'opération) ;
 * 3. écartée par un humain, tant qu'elle est sans justificatif ;
 * 4. hors champ ;
 * 5. justifiée chez la source : la pièce reconnue par un humain ou par la règle ; déposable, elle
 *    est marquée présente (à blanc compris) ; présente ou déposée, retenue ; non déposable, laissée
 *    en candidate avec son motif, jamais marquée ; jamais une pièce que le registre reconnaît sur
 *    une autre opération ;
 * 6. sûre : pièce déposable, déposer et relier ; déposée pour cette opération, relier ; présente
 *    ou non déposable, proposée avec le motif ; de même pour une opération à trancher (ou sûre)
 *    dont un humain a choisi la pièce (« C'est celle-ci », décision du 13/10/2026), encore parmi
 *    ses candidates (ou sa pièce sûre), dite `choisie` ; ventilée, provisoire ou validée chez la
 *    source, elle reste proposée avec ce motif ;
 * 7. proposée, à fournir.
 * Un choix devenu caduc (pièce plus candidate, opération justifiée chez la source, reliée,
 * écartée, hors champ, hors fenêtre, à fournir) est ignoré. Une pièce n'est jamais retenue pour
 * deux opérations : la pièce d'un choix qui peut se relier (déposable, ou déposée pour son
 * opération, et non reconnue au registre ailleurs) est réservée à son opération avant toute
 * décision de la règle, le premier choix dans l'ordre des issues gardant une pièce choisie deux
 * fois. En écriture seulement, et la relecture confirmée, les sûres (choisies comprises)
 * deviennent des actions, triées par jour, `MAX_ECRITURES_PAR_PASSAGE` au plus.
 */
export function planDesJustificatifs(e: EntreePlanJustificatifs): PlanJustificatifs {
  const etatDe = (pieceId: string) => e.etatsPieces.get(pieceId) ?? INCONNUE
  const retenues = new Set<string>()
  const marquer: PieceAMarquer[] = []
  const lignesARelier = new Map<string, string>()
  // Une pièce reconnue au registre (ligne `present`, d'un humain ou d'un passage précédent) est à
  // son opération : la règle ne la donne jamais à une autre.
  const reconnuesAuRegistre = new Map<string, string>()
  for (const [ref, lignes] of e.registre)
    for (const l of lignes)
      if (l.statut === 'present' && !reconnuesAuRegistre.has(l.pieceId))
        reconnuesAuRegistre.set(l.pieceId, ref)

  // Choix d'un humain encore en vue : opération à trancher ou à fournir dont la pièce choisie,
  // candidate ou trouvée par la recherche (version 11), reste dans la frontière (son état de dépôt
  // est connu), ou sûre sur cette pièce ; ni reliée ni écartée. Sinon caduc, ignoré.
  const choixEnVue = (ref: string, issue: IssueJustificatif): string | null => {
    const p = e.choix?.get(ref)
    if (p === undefined || e.ecartees.has(ref)) return null
    if ((e.registre.get(ref) ?? []).some((l) => l.statut === 'relie')) return null
    if (issue.issue === 'sure') return issue.pieceId === p ? p : null
    if (issue.issue === 'proposee' && issue.candidates.some((c) => c.pieceId === p)) return p
    if (issue.issue === 'proposee' || issue.issue === 'a_fournir')
      return e.etatsPieces.has(p) ? p : null
    return null
  }
  // Ce qui retient l'opération d'un choix : ventilée, provisoire ou validée chez la source.
  const operationRetenue = (ref: string): 'ventilee' | 'provisoire' | 'validee' | null => {
    const op = e.operations.get(ref)
    if (op?.ventilee) return 'ventilee'
    if (op?.provisoire) return 'provisoire'
    if (op?.validee) return 'validee'
    return null
  }
  const choisies = new Map<string, string>()
  // Pièce d'un choix qui se relie, réservée à son opération avant la règle.
  const reservees = new Map<string, string>()
  for (const [ref, issue] of e.issues) {
    const p = choixEnVue(ref, issue)
    if (p === null) continue
    choisies.set(ref, p)
    if (reservees.has(p) || operationRetenue(ref) !== null) continue
    if ((reconnuesAuRegistre.get(p) ?? ref) !== ref) continue
    const d = etatDe(p)
    if (d.etat === 'deposable' || (d.etat === 'deposee' && d.operation === ref)) {
      reservees.set(p, ref)
      retenues.add(p)
    }
  }

  const planifier = (ref: string, issue: IssueJustificatif): OperationPlanifiee => {
    const planifiee = (
      etat: EtatOperationPortail,
      o: Partial<Omit<OperationPlanifiee, 'ref' | 'etat'>> = {},
    ): OperationPlanifiee => ({
      ref,
      etat,
      pieceId: null,
      action: null,
      candidates: [],
      motif: null,
      ...o,
    })
    if (issue.issue === 'hors_fenetre') return planifiee('hors_fenetre')
    const lignes = e.registre.get(ref) ?? []
    const reliee = lignes.find((l) => l.statut === 'relie')
    if (reliee) return planifiee('reliee', { pieceId: reliee.pieceId })
    if (e.ecartees.has(ref) && (e.operations.get(ref)?.justificatifs ?? 0) === 0)
      return planifiee('ecartee')

    // Choix d'un humain : traité comme une sûre sur la pièce choisie, mêmes contrôles de dépôt.
    const choisie = choisies.get(ref)
    if (
      choisie !== undefined &&
      (issue.issue === 'sure' || issue.issue === 'proposee' || issue.issue === 'a_fournir')
    ) {
      const candidates: PieceEnVue[] =
        issue.issue === 'sure'
          ? [pieceSure(issue)]
          : issue.issue === 'proposee'
            ? issue.candidates.map(enVue)
            : []
      if (reservees.get(choisie) === ref) {
        const d = etatDe(choisie)
        if (d.etat === 'deposee') {
          lignesARelier.set(ref, d.justificatifId)
          return planifiee('sure', {
            pieceId: choisie,
            action: 'relier',
            candidates,
            choisie: true,
          })
        }
        return planifiee('sure', {
          pieceId: choisie,
          action: 'deposer_et_relier',
          candidates,
          choisie: true,
        })
      }
      const d = etatDe(choisie)
      const motif: MotifPlanJustificatif =
        operationRetenue(ref) ??
        (d.etat === 'presente'
          ? 'deja_chez_cabinet'
          : d.etat === 'non_deposable'
            ? d.motif
            : d.etat === 'deposee' && d.operation !== ref
              ? 'deposee_ailleurs'
              : 'piece_disputee')
      return planifiee('proposee', { candidates, motif })
    }

    switch (issue.issue) {
      case 'hors_champ':
        return planifiee('hors_champ', { motif: issue.motif })
      case 'justifiee': {
        const candidates = issue.candidates.map(enVue)
        // Reconnue par un humain (ou par un passage précédent) : la ligne `present` fait foi.
        const reconnue = lignes.find((l) => l.statut === 'present')?.pieceId
        if (reconnue !== undefined && !retenues.has(reconnue)) {
          retenues.add(reconnue)
          return planifiee('justifiee', { pieceId: reconnue, candidates })
        }
        const p = issue.pieceReconnue
        if (p === null || retenues.has(p) || (reconnuesAuRegistre.get(p) ?? ref) !== ref)
          return planifiee('justifiee', { candidates })
        const d = etatDe(p)
        if (d.etat === 'non_deposable')
          return planifiee('justifiee', { candidates, motif: d.motif })
        retenues.add(p)
        if (d.etat === 'deposable') marquer.push({ pieceId: p, operation: ref })
        return planifiee('justifiee', { pieceId: p, candidates })
      }
      case 'sure': {
        const p = issue.pieceId
        const candidates: PieceEnVue[] = [pieceSure(issue)]
        if (retenues.has(p)) return planifiee('proposee', { candidates, motif: 'piece_disputee' })
        const d = etatDe(p)
        if (d.etat === 'presente')
          return planifiee('proposee', { candidates, motif: 'deja_chez_cabinet' })
        if (d.etat === 'non_deposable') return planifiee('proposee', { candidates, motif: d.motif })
        if (d.etat === 'deposee' && d.operation !== ref)
          return planifiee('proposee', { candidates, motif: 'deposee_ailleurs' })
        retenues.add(p)
        if (d.etat === 'deposee') {
          lignesARelier.set(ref, d.justificatifId)
          return planifiee('sure', { pieceId: p, action: 'relier', candidates })
        }
        return planifiee('sure', { pieceId: p, action: 'deposer_et_relier', candidates })
      }
      case 'proposee':
        return planifiee('proposee', {
          candidates: issue.candidates.map(enVue),
          motif: issue.motif,
        })
      case 'a_fournir':
        return planifiee('a_fournir')
    }
  }

  const operations = [...e.issues].map(([ref, issue]) => planifier(ref, issue))

  const plafond = Math.max(
    0,
    Math.min(Math.floor(e.maxActions ?? MAX_ECRITURES_PAR_PASSAGE), MAX_ECRITURES_PAR_PASSAGE),
  )
  const actions =
    e.mode === 'ecriture' && e.relecture === 'ok'
      ? operations
          .flatMap((o): ActionPlanJustificatif[] => {
            const op = e.operations.get(o.ref)
            if (o.etat !== 'sure' || o.action === null || !op) return []
            const justificatifId = lignesARelier.get(o.ref)
            return [
              {
                operation: o.ref,
                action: o.action,
                date: op.date,
                montantCts: op.montantCts,
                devise: op.devise,
                ...(justificatifId === undefined ? {} : { justificatifId }),
              },
            ]
          })
          .sort((a, b) => comparer(a.date, b.date) || comparer(a.operation, b.operation))
          .slice(0, plafond)
      : []

  const comptes = Object.fromEntries(
    ETATS_OPERATION_PORTAIL.map((x) => [x, 0]),
  ) as ComptesJustificatifs
  for (const o of operations) comptes[o.etat]++
  return { operations, marquer, actions, comptes }
}

/** Les comptes d'un passage, gardés sur la collecte et rendus à l'agent (des nombres seulement). */
export type BilanJustificatifs = {
  /** Opérations lues, par état. */
  etats: ComptesJustificatifs
  /** Pièces reconnues et marquées « déjà chez le cabinet » par ce passage. */
  marquees: number
  /** Actions d'écriture rendues à l'agent (aucune à blanc). */
  actions: number
  /** Pièces de la fenêtre de décision sans opération en vue. */
  piecesSansOperation: number
  /**
   * Justificatifs chez le portail sans liaison, listés à part ; null : non lus. Ceux arrivés
   * depuis `documentsSansLiaisonDepuis` quand il est donné, sinon tous (agent d'avant 0.5.1).
   */
  documentsSansLiaison: number | null
  /** Premier jour de l'exercice examiné, d'où se comptent les précédents ; null : tous ensemble. */
  documentsSansLiaisonDepuis?: string | null
  /** Ceux arrivés avant, l'historique ; null : non séparés. */
  documentsSansLiaisonAnterieurs?: number | null
}

export function bilanDuPlan(
  plan: PlanJustificatifs,
  o: { piecesSansOperation: number; documentsSansLiaison: number | null },
): BilanJustificatifs {
  return {
    etats: { ...plan.comptes },
    marquees: plan.marquer.length,
    actions: plan.actions.length,
    piecesSansOperation: o.piecesSansOperation,
    documentsSansLiaison: o.documentsSansLiaison,
  }
}

// ---------------------------------------------------------------------------------------------
// Mode d'un passage : huit verrous
// ---------------------------------------------------------------------------------------------

/**
 * Plus ancienne version de l'agent du Mac qui sait écrire chez tous les portails de justificatifs
 * (0.6.30 : Dougs, en plus de Tiime depuis 0.6.0).
 */
export const VERSION_ECRITURE_AGENT = '0.6.30'

/** Le premier verrou qui manque pour écrire. */
export const MOTIFS_MODE_JUSTIFICATIFS = [
  'via_assistant',
  'autre_personne',
  'passage_non_valide',
  'autre_version',
  'ecriture_fermee',
  'ecritures_coupees',
  'entite_hors_depot',
  'portail_sans_ecriture',
  'agent_ancien',
  'premier_passage_manuel',
] as const
export type MotifModeJustificatifs = (typeof MOTIFS_MODE_JUSTIFICATIFS)[number]

export const LIBELLES_MODE_JUSTIFICATIFS: Readonly<Record<MotifModeJustificatifs, string>> = {
  via_assistant: 'Demandé via l’assistant',
  autre_personne: 'Demandé par une autre personne que le titulaire',
  passage_non_valide: 'Passage à blanc pas encore validé par le titulaire',
  autre_version: 'Passage à blanc validé pour une autre version de la règle : à revalider',
  ecriture_fermee: 'Écriture fermée sur cette connexion',
  ecritures_coupees: 'Écritures coupées par la configuration (portails.ecritures)',
  entite_hors_depot: 'L’entité n’est pas en dépôt direct',
  portail_sans_ecriture: 'Ce portail n’a pas d’écriture déclarée : rien ne s’y écrit',
  agent_ancien: `Agent du Mac trop ancien pour écrire (${VERSION_ECRITURE_AGENT} au moins)`,
  premier_passage_manuel:
    'Passage mensuel à blanc : un premier passage en écriture, demandé par le titulaire, doit d’abord aboutir depuis l’ouverture de l’écriture',
}

/** Ce que le mode d'un passage vérifie, dans l'ordre. */
export type VerrousJustificatifs = {
  /** Plafond posé à la création : écriture pour le titulaire ou le job mensuel, sinon à blanc. */
  plafond: ModeJustificatifs
  /** Demandé via l'assistant : toujours à blanc. */
  viaAssistant: boolean
  /** Un passage à blanc de la connexion a été validé par le titulaire. */
  passageValide: boolean
  /** Version de la règle de cette validation. */
  versionValidee: number | null
  /** Écriture ouverte sur la connexion (et pas coupée depuis). */
  ecritureOuverte: boolean
  /** Coupe-circuit de déploiement `portails.ecritures`. */
  ecrituresPermises: boolean
  /** L'entité de la connexion est en dépôt direct. */
  entiteEnDepot: boolean
  /** Le fournisseur de la connexion déclare le dépôt et la liaison (`portailQuiEcrit`). */
  portailEcrit: boolean
  /** Version de l'agent du Mac qui tient la collecte ; inconnue : null. */
  versionAgent: string | null
  /** Passage mensuel (planifié par le job), pas demandé par une personne. */
  mensuel: boolean
  /**
   * Un passage demandé par le titulaire, en écriture, s'est terminé depuis la dernière ouverture
   * de l'écriture de la connexion : seul compte pour un passage mensuel.
   */
  premierPassageManuelFait: boolean
}

export type ResultatModeJustificatifs =
  { mode: 'ecriture'; motif: null } | { mode: 'a_blanc'; motif: MotifModeJustificatifs }

/**
 * Écriture seulement si tous les verrous sont ouverts ; sinon à blanc, avec le premier qui
 * manque : plafond (assistant, autre personne), passage à blanc non validé, validé pour une autre
 * version de la règle, écriture fermée sur la connexion, `portails.ecritures` faux, entité hors
 * du dépôt direct, portail sans écriture déclarée, agent trop ancien, et pour
 * un passage mensuel, aucun passage manuel en écriture terminé depuis l'ouverture (décision de la
 * nuit du 12-13/10/2026 : le premier passage réel se fait à la main, titulaire présent). Figé à la
 * prise, recalculé avant chaque écriture.
 */
export function modeJustificatifs(v: VerrousJustificatifs): ResultatModeJustificatifs {
  const aBlanc = (motif: MotifModeJustificatifs): ResultatModeJustificatifs => ({
    mode: 'a_blanc',
    motif,
  })
  if (v.viaAssistant) return aBlanc('via_assistant')
  if (v.plafond !== 'ecriture') return aBlanc('autre_personne')
  if (!v.passageValide) return aBlanc('passage_non_valide')
  if (v.versionValidee !== VERSION_REGLE_JUSTIFICATIFS) return aBlanc('autre_version')
  if (!v.ecritureOuverte) return aBlanc('ecriture_fermee')
  if (!v.ecrituresPermises) return aBlanc('ecritures_coupees')
  if (!v.entiteEnDepot) return aBlanc('entite_hors_depot')
  if (!v.portailEcrit) return aBlanc('portail_sans_ecriture')
  if (v.versionAgent === null || !versionSuffisante(v.versionAgent, VERSION_ECRITURE_AGENT))
    return aBlanc('agent_ancien')
  if (v.mensuel && !v.premierPassageManuelFait) return aBlanc('premier_passage_manuel')
  return { mode: 'ecriture', motif: null }
}

/**
 * Le fournisseur déclare-t-il les écritures du dépôt puis de la liaison (`ECRITURES_METIER`,
 * toutes les capacités de `deposer_et_relier`, en une requête ou en deux) ? Faux pour un portail de
 * documents, un portail de justificatifs en lecture ou un inconnu : l'écriture ne s'y ouvre pas,
 * et un passage y reste à blanc.
 */
export function portailQuiEcrit(fournisseur: string): boolean {
  const capacites = new Set<CapaciteEcriture>(
    ecrituresMetierDe(fournisseur).flatMap((e) =>
      e.relieAussi ? [e.capacite, 'relier_justificatif'] : [e.capacite],
    ),
  )
  return CAPACITES_ACTION_JUSTIFICATIF.deposer_et_relier.every((c) => capacites.has(c))
}

// ---------------------------------------------------------------------------------------------
// Fenêtre et passage mensuel
// ---------------------------------------------------------------------------------------------

/**
 * Premiers mois d'un exercice où l'exercice précédent est encore décidé : son bilan n'est pas
 * bouclé (décision de Pierre-Marie du 13/10/2026 : de janvier à mai pour une clôture au 31/12).
 */
export const MOIS_EXERCICE_PRECEDENT_JUSTIFICATIFS = 5
/**
 * Jours lus avant la fenêtre de décision : ils comptent pour l'unicité, jamais décidés. Toute
 * opération concordante d'une pièce d'une opération décidée y est lue : la pièce peut précéder
 * son opération de toute la fenêtre large (60 jours), et une autre opération précéder la pièce de
 * 15 jours.
 */
export const MARGE_LECTURE_JOURS = FENETRE_LARGE_JUSTIFICATIF.max - FENETRE_LARGE_JUSTIFICATIF.min

/** Jours AAAA-MM-JJ (Paris) : lus de `lectureDu`, décidés de `decisionDu`, jusqu'à `au`. */
export type FenetreJustificatifs = { lectureDu: string; decisionDu: string; au: string }

/**
 * Décidées depuis le premier jour de l'exercice en cours de l'entité (sa clôture MM-JJ, sinon
 * l'année civile : `debutExercice`), ou de l'exercice précédent pendant les cinq premiers mois de
 * l'exercice (clôture au 31 décembre : jusqu'au 31 mai ; au 30 juin : jusqu'au 30 novembre) ;
 * lues avec `MARGE_LECTURE_JOURS` de marge, jusqu'à aujourd'hui.
 */
export function fenetreJustificatifs(
  aujourdhui: string,
  cloture: string | null,
): FenetreJustificatifs {
  if (!jourReel(aujourdhui)) throw new RangeError(`Jour invalide : ${aujourdhui}`)
  const exercice = debutExercice(cloture, aujourdhui)
  const decisionDu =
    aujourdhui < ajouterMois(exercice, MOIS_EXERCICE_PRECEDENT_JUSTIFICATIFS)
      ? debutExercice(cloture, ajouterJours(exercice, -1))
      : exercice
  return {
    lectureDu: ajouterJours(decisionDu, -MARGE_LECTURE_JOURS),
    decisionDu,
    au: aujourdhui,
  }
}

/** Passage mensuel : du 2 au 10 du mois, chaque jour à partir de 9 h (Paris). */
export const JOUR_PASSAGE_MENSUEL = 2
export const DERNIER_JOUR_PASSAGE_MENSUEL = 10
export const HEURE_PASSAGE_MENSUEL = '09:00'

/** Le passage mensuel peut-il partir à cet instant ? (Le job vérifie le reste : un par jour.) */
export function passageMensuelDu(maintenant: Date): boolean {
  const jour = jourAParis(maintenant)
  const quantieme = Number(jour.slice(8, 10))
  if (quantieme < JOUR_PASSAGE_MENSUEL || quantieme > DERNIER_JOUR_PASSAGE_MENSUEL) return false
  return maintenant.getTime() >= Date.parse(instantDeParis(jour, HEURE_PASSAGE_MENSUEL))
}

// ---------------------------------------------------------------------------------------------
// Registre des dépôts : machine d'états d'une ligne
// ---------------------------------------------------------------------------------------------

/** Qui fait avancer une ligne : le serveur, l'agent du Mac, un humain dans l'appli, la règle (N0). */
export const ACTEURS_JUSTIFICATIF = ['serveur', 'agent', 'humain', 'regle'] as const
export type ActeurJustificatif = (typeof ACTEURS_JUSTIFICATIF)[number]

export const EVENEMENTS_JUSTIFICATIF = [
  'reserver',
  'remettre',
  'depose',
  'relie',
  'liaison_echouee',
  'orpheliner',
  'echec',
  'abandonner',
  'incertain',
  'reconnaitre',
  'arrivee',
  'renvoyer',
  'contester',
] as const
export type TypeEvenementJustificatif = (typeof EVENEMENTS_JUSTIFICATIF)[number]
export type EvenementJustificatif = { type: TypeEvenementJustificatif; par: ActeurJustificatif }

/**
 * Effets appliqués dans la même transaction que la ligne :
 * - reserver_piece : `transmise_le` posé sur la pièce ;
 * - noter_remise : `remis_le` posé sur la ligne (fichier remis à l'agent, une fois) ;
 * - transmission_faite : pièce `faite`, `transmise_a`, `transmission_ref` = le document ;
 * - compter_liaison : tentatives de liaison + 1 (orpheline au `ESSAIS_LIAISON_JUSTIFICATIF`-ième) ;
 * - tache_a_verifier : tâche « à vérifier » ;
 * - liberer_piece : `transmise_le` nul ;
 * - compter_echec : tentatives + 1, alerte au troisième ;
 * - transmission_incertaine et ouvrir_decision : pièce `incertain`, décision « arrivé ? » ;
 * - deja_chez_cabinet : pièce `faite`, « déjà chez le cabinet » ;
 * - transmission_constatee : pièce `faite` (arrivée constatée par un humain) ;
 * - fermer_decision : décision « arrivé ? » fermée ;
 * - transmission_a_faire : pièce `a_faire` ;
 * - recalculer_transmission : transmission de la pièce recalculée.
 */
export type EffetJustificatif =
  | 'reserver_piece'
  | 'noter_remise'
  | 'transmission_faite'
  | 'compter_liaison'
  | 'tache_a_verifier'
  | 'liberer_piece'
  | 'compter_echec'
  | 'transmission_incertaine'
  | 'ouvrir_decision'
  | 'deja_chez_cabinet'
  | 'transmission_constatee'
  | 'fermer_decision'
  | 'transmission_a_faire'
  | 'recalculer_transmission'

/** Une ligne : son statut (null : aucune ligne vivante pour la pièce), et son fichier remis. */
export type EtatLigneJustificatif = { statut: StatutJustificatif | null; remis: boolean }

type DepartJustificatif = { readonly statut: StatutJustificatif | null; readonly remis?: boolean }

export type LigneTransitionJustificatif = {
  evenement: TypeEvenementJustificatif
  /** Départs permis ; `remis` absent : remise ou non. */
  depuis: readonly DepartJustificatif[]
  vers: StatutJustificatif
  par: readonly ActeurJustificatif[]
  effets: readonly EffetJustificatif[]
}

/** Au troisième échec de liaison, le serveur rend la ligne orpheline. */
export const ESSAIS_LIAISON_JUSTIFICATIF = 3

export const TRANSITIONS_JUSTIFICATIF: readonly LigneTransitionJustificatif[] = [
  {
    evenement: 'reserver',
    depuis: [{ statut: null }],
    vers: 'reserve',
    par: ['serveur'],
    effets: ['reserver_piece'],
  },
  {
    evenement: 'remettre',
    depuis: [{ statut: 'reserve', remis: false }],
    vers: 'reserve',
    par: ['serveur'],
    effets: ['noter_remise'],
  },
  /** Un dépôt suppose le fichier remis ; tardif, il fait passer une ligne incertaine à déposée. */
  {
    evenement: 'depose',
    depuis: [{ statut: 'reserve', remis: true }, { statut: 'incertain' }],
    vers: 'depose',
    par: ['agent'],
    effets: ['transmission_faite'],
  },
  { evenement: 'relie', depuis: [{ statut: 'depose' }], vers: 'relie', par: ['agent'], effets: [] },
  {
    evenement: 'liaison_echouee',
    depuis: [{ statut: 'depose' }],
    vers: 'depose',
    par: ['agent'],
    effets: ['compter_liaison'],
  },
  {
    evenement: 'orpheliner',
    depuis: [{ statut: 'depose' }],
    vers: 'orphelin',
    par: ['agent', 'serveur'],
    effets: ['tache_a_verifier'],
  },
  {
    evenement: 'echec',
    depuis: [{ statut: 'reserve' }],
    vers: 'echec',
    par: ['agent', 'serveur'],
    effets: ['liberer_piece', 'compter_echec'],
  },
  {
    evenement: 'abandonner',
    depuis: [{ statut: 'reserve' }],
    vers: 'annule',
    par: ['agent', 'serveur'],
    effets: ['liberer_piece'],
  },
  {
    evenement: 'incertain',
    depuis: [{ statut: 'reserve', remis: true }],
    vers: 'incertain',
    par: ['agent', 'serveur'],
    effets: ['transmission_incertaine', 'ouvrir_decision'],
  },
  {
    evenement: 'reconnaitre',
    depuis: [{ statut: null }],
    vers: 'present',
    par: ['regle', 'humain'],
    effets: ['deja_chez_cabinet'],
  },
  {
    evenement: 'arrivee',
    depuis: [{ statut: 'incertain' }],
    vers: 'present',
    par: ['humain'],
    effets: ['transmission_constatee', 'fermer_decision'],
  },
  {
    evenement: 'renvoyer',
    depuis: [{ statut: 'incertain' }],
    vers: 'annule',
    par: ['humain'],
    effets: ['transmission_a_faire', 'fermer_decision'],
  },
  /** Seule une pièce reconnue (jamais remise à l'agent) se conteste. */
  {
    evenement: 'contester',
    depuis: [{ statut: 'present', remis: false }],
    vers: 'annule',
    par: ['humain'],
    effets: ['recalculer_transmission'],
  },
]

export class TransitionJustificatifInvalide extends Error {}

/** (ligne, événement) vers (ligne, effets) ; refuse un départ ou un acteur hors du tableau. */
export function transitionJustificatif(
  etat: EtatLigneJustificatif,
  evenement: EvenementJustificatif,
): { statut: StatutJustificatif; remis: boolean; effets: EffetJustificatif[] } {
  const ligne = TRANSITIONS_JUSTIFICATIF.find(
    (l) =>
      l.evenement === evenement.type &&
      l.depuis.some(
        (d) => d.statut === etat.statut && (d.remis === undefined || d.remis === etat.remis),
      ),
  )
  if (!ligne) {
    const depuis =
      etat.statut === null ? 'aucune ligne' : `${etat.statut}${etat.remis ? ', remise' : ''}`
    throw new TransitionJustificatifInvalide(
      `Registre des justificatifs : « ${evenement.type} » impossible depuis « ${depuis} ».`,
    )
  }
  if (!ligne.par.includes(evenement.par))
    throw new TransitionJustificatifInvalide(
      `Registre des justificatifs : « ${evenement.type} » n’est pas permis à « ${evenement.par} ».`,
    )
  return {
    statut: ligne.vers,
    remis: etat.statut !== null && (etat.remis || evenement.type === 'remettre'),
    effets: [...ligne.effets],
  }
}

// ---------------------------------------------------------------------------------------------
// Étape d'écriture : réserver, recevoir le fichier, déposer puis relier, rendre compte
// ---------------------------------------------------------------------------------------------
// L'agent du Mac ne touche qu'aux « sûres » du plan de la collecte qu'il tient, jamais à une
// proposition. Pour chacune : relire l'opération (`refusAvantEcriture`), réserver la pièce (le
// serveur rejoue le plan et la frontière), recevoir le fichier une fois et vérifier son empreinte,
// déposer (requête armée), rendre compte, puis relier (feu vert recalculé, requête armée) et rendre
// compte. Le registre tient chaque issue (`suiteEtapeJustificatif`) : réservée avant l'envoi,
// « incertaine » quand l'issue manque, jamais redéposée d'office ; déjà déposée, elle se relie sans
// se redéposer, tant que la règle dit l'opération sûre sur sa pièce (`refusLiaison`) ; sinon son
// document se relit et se constate seulement (`verifier`), jamais il ne se relie. Rien d'autre ne
// s'écrit chez le portail.

/**
 * Ce que l'agent fait d'une opération d'un passage en écriture : les deux actions du plan
 * (déposer puis relier une sûre, relier une sûre dont la pièce est déjà déposée pour elle), et
 * `verifier` : le document déjà déposé d'une opération que la règle ne dit plus sûre sur sa pièce
 * se relit et se constate (relié à cette opération : noté ; à une autre : orphelin ; sinon un
 * humain tranche), jamais il ne se relie. Le plan, et la colonne de l'opération, ne connaissent
 * que `ACTIONS_JUSTIFICATIF`.
 */
export const ACTIONS_PASSAGE_JUSTIFICATIF = [...ACTIONS_JUSTIFICATIF, 'verifier'] as const
export type ActionPassageJustificatif = (typeof ACTIONS_PASSAGE_JUSTIFICATIF)[number]

/** Une action rendue à l'agent : une action du plan, ou la vérification d'un document déposé. */
export type ActionRendueJustificatif = Omit<ActionPlanJustificatif, 'action'> & {
  action: ActionPassageJustificatif
}

/** Ce que fait chaque action, dans l'ordre : déposer puis relier, ou relier seulement. */
export const CAPACITES_ACTION_JUSTIFICATIF: Readonly<
  Record<ActionJustificatif, readonly CapaciteEcriture[]>
> = {
  deposer_et_relier: ['deposer_justificatif', 'relier_justificatif'],
  relier: ['relier_justificatif'],
}

/** L'agent demande la pièce d'une opération du plan de la collecte qu'il tient. */
export const reservationJustificatif = z.object({ operation: refExterne }).strict()
export type ReservationJustificatif = z.infer<typeof reservationJustificatif>

/**
 * Refus d'une réservation, rendus en 200 (le 409 reste à la prise perdue) : écriture coupée (mode
 * recalculé à blanc), opération hors du plan de la collecte (ou plus sûre sur la même pièce), pièce
 * plus déposable (frontière rejouée), catégorie de dépôt inconnue pour ce passage, opération ou
 * pièce déjà prise au registre.
 */
export const REFUS_RESERVATION_JUSTIFICATIF = [
  'ecriture_coupee',
  'hors_plan',
  'piece_indisponible',
  'categorie_inconnue',
  'operation_prise',
  'piece_prise',
] as const
export type RefusReservationJustificatif = (typeof REFUS_RESERVATION_JUSTIFICATIF)[number]

/**
 * Nom du fichier rendu par la réservation (`nomFichierPiece`) et envoyé au portail : ASCII, sans
 * guillemet, barre ni retour à la ligne (il entre dans un en-tête multipart), 150 signes au plus.
 */
export const NOM_FICHIER_JUSTIFICATIF = /^[A-Za-z0-9_,-][A-Za-z0-9._,-]{0,149}$/

/**
 * Le nom d'une pièce (`nomFichierPiece`) rendu sûr pour le dépôt : accents retirés, toute suite de
 * signes hors de `NOM_FICHIER_JUSTIFICATIF` changée en un tiret, sans point ni tiret de tête, 150
 * signes au plus en gardant l'extension ; rien de lisible : « justificatif ».
 */
export function nomFichierJustificatif(nom: string): string {
  const propre = nom
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^A-Za-z0-9._,-]+/g, '-')
    .replace(/^[.-]+/, '')
  if (!/[A-Za-z0-9]/.test(propre)) return 'justificatif'
  if (propre.length <= 150) return propre
  const extension = /\.[A-Za-z0-9]{1,5}$/.exec(propre)?.[0] ?? ''
  return propre.slice(0, 150 - extension.length) + extension
}

/** La pièce réservée pour cette prise et cet ordinateur, ou un refus. */
export const reponseReservationJustificatif = z.discriminatedUnion('ok', [
  z
    .object({
      ok: z.literal(true),
      /** La ligne du registre (le justificatif). */
      justificatifId: z.uuid(),
      nom: z.string().regex(NOM_FICHIER_JUSTIFICATIF, 'Nom de fichier illisible.'),
      typeMime: z.enum(TYPES_MIME_DEPOT_DIRECT),
      taille: z.int().min(1).max(TAILLE_MAX_COLLECTE),
      /**
       * Dossier épinglé et catégorie de dépôt du passage (références chez le portail) ; sans
       * catégorie chez un portail qui n'en a pas (Dougs).
       */
      dossier: refExterne,
      categorie: refExterne.nullable(),
    })
    .strict(),
  z.object({ ok: z.literal(false), refus: z.enum(REFUS_RESERVATION_JUSTIFICATIF) }).strict(),
])
export type ReponseReservation = z.infer<typeof reponseReservationJustificatif>

/** Ce que la réservation relit, au moment de réserver (mode et plan, opération, pièce). */
export type VerrousReservation = {
  /** Mode recalculé à la réservation (un verrou tombé en cours de passage arrête tout). */
  mode: ModeJustificatifs
  /** L'action du plan de cette collecte pour l'opération ; null : absente du plan. */
  actionDuPlan: ActionJustificatif | null
  /** L'opération lue, telle que le dernier calcul l'a laissée : son état et sa pièce. */
  etat: EtatOperationPortail | null
  pieceId: string | null
  /** Écartée par un humain depuis, ou sa pièce exclue (« Ce n'est pas cette pièce »). */
  ecartee: boolean
  pieceExclue: boolean
  /** État de dépôt de la pièce, frontière rejouée ; null : hors de la frontière. */
  etatPiece: EtatDepot | null
  /** Catégorie de dépôt connue pour ce passage. */
  categorieConnue: boolean
  /** Le portail exige une catégorie de dépôt (`categorieDepotExigee` : Tiime, pas Dougs). */
  categorieExigee: boolean
}

/**
 * La réservation d'une pièce pour l'agent, dans cet ordre : écriture recalculée (sinon
 * `ecriture_coupee`) ; opération du plan de la collecte à déposer puis relier, encore sûre sur sa
 * pièce, ni écartée ni sa pièce exclue (sinon `hors_plan`) ; pièce déposable, frontière rejouée
 * (sinon `piece_indisponible`) ; catégorie de dépôt connue, chez un portail qui l'exige (Tiime ;
 * chez Dougs, une catégorie nulle est normale). L'opération ou la pièce déjà prise au registre
 * (`operation_prise`, `piece_prise`) se lit à l'écriture de la ligne (index uniques).
 */
export function refusReservation(v: VerrousReservation): RefusReservationJustificatif | null {
  if (v.mode !== 'ecriture') return 'ecriture_coupee'
  if (
    v.actionDuPlan !== 'deposer_et_relier' ||
    v.etat !== 'sure' ||
    v.pieceId === null ||
    v.ecartee ||
    v.pieceExclue
  )
    return 'hors_plan'
  if (v.etatPiece?.etat !== 'deposable') return 'piece_indisponible'
  return v.categorieConnue || !v.categorieExigee ? null : 'categorie_inconnue'
}

/**
 * La catégorie lue au passage permet-elle un dépôt ? Chez un portail qui exige une catégorie de
 * dépôt (`categorieDepotExigee` : Tiime), seulement s'il l'a lue (null : introuvable ou ambiguë,
 * aucun dépôt possible) ; chez un portail qui n'en a pas (Dougs), toujours. Lu par le plan (aucune
 * action de dépôt), le bilan (aucune sûre dite « relierait seule ») et la réservation.
 */
export function depotPermisParCategorie(c: {
  exigee: boolean
  categorie: string | null | undefined
}): boolean {
  return !c.exigee || typeof c.categorie === 'string'
}

/**
 * Pourquoi le serveur ne remet pas le fichier d'une pièce réservée : illisible au stockage (un
 * essai plus tard), d'un type ou d'une taille que le dépôt direct ne prend pas, ou d'une autre
 * empreinte que la pièce (le fichier a changé, ou l'empreinte de la pièce n'est pas celle de ses
 * octets).
 */
export const ECHECS_REMISE_FICHIER = ['fichier_absent', 'fichier_refuse', 'empreinte'] as const
export type EchecRemiseFichier = (typeof ECHECS_REMISE_FICHIER)[number]
/** Ceux-là ne se retentent pas : la pièce se dépose à la main, jamais un essai à chaque passage. */
export const ECHECS_REMISE_A_LA_MAIN: readonly EchecRemiseFichier[] = [
  'fichier_refuse',
  'empreinte',
]

/**
 * Le fichier d'une pièce réservée, lu par le serveur avant sa remise unique : lisible, PDF, JPEG
 * ou PNG à ses premiers octets, 8 Mo au plus, et l'empreinte SHA-256 de ses octets égale à celle
 * de la pièce. Sinon le motif de l'échec.
 */
export function echecRemiseFichier(f: {
  /** Ce que le serveur a lu : le type d'après les premiers octets, la taille, l'empreinte. */
  lu: { typeLu: string | null; taille: number; empreinte: string } | null
  empreintePiece: string | null
}): EchecRemiseFichier | null {
  if (f.lu === null) return 'fichier_absent'
  const { typeLu, taille, empreinte } = f.lu
  if (
    typeLu === null ||
    !(TYPES_MIME_DEPOT_DIRECT as readonly string[]).includes(typeLu) ||
    taille <= 0 ||
    taille > TAILLE_MAX_COLLECTE
  )
    return 'fichier_refuse'
  return f.empreintePiece !== null && empreinte === f.empreintePiece ? null : 'empreinte'
}

/** Empreinte SHA-256 d'un fichier, en hexadécimal minuscule. */
export const EMPREINTE_SHA256 = /^[0-9a-f]{64}$/

/**
 * Ce que la remise annonce du fichier (empreinte de l'en-tête `x-empreinte`, taille, type) :
 * l'agent le revalide, puis le compare aux octets reçus (`fichierRemisConforme`).
 */
export const fichierRemisJustificatif = z
  .object({
    empreinte: z.string().regex(EMPREINTE_SHA256, 'Empreinte SHA-256 illisible.'),
    taille: z.int().min(1).max(TAILLE_MAX_COLLECTE),
    typeMime: z.enum(TYPES_MIME_DEPOT_DIRECT),
  })
  .strict()
export type FichierRemis = z.infer<typeof fichierRemisJustificatif>

/**
 * Le fichier reçu est-il celui que la réservation annonçait ? Taille et type annoncés à la
 * réservation et à la remise ; sur les octets reçus, la taille, le type lu aux premiers octets et
 * l'empreinte calculée par l'agent. Sinon, abandon `empreinte` : rien n'est déposé.
 */
export function fichierRemisConforme(
  reservation: { taille: number; typeMime: string },
  remis: FichierRemis,
  recu: { taille: number; empreinte: string; typeLu: string | null },
): boolean {
  return (
    remis.taille === reservation.taille &&
    remis.typeMime === reservation.typeMime &&
    recu.taille === reservation.taille &&
    recu.typeLu === reservation.typeMime &&
    recu.empreinte === remis.empreinte
  )
}

/** Identifiant d'un document chez le portail : des chiffres seulement, 1 à 15. */
export const DOCUMENT_PORTAIL = /^[0-9]{1,15}$/
const documentPortail = z.string().regex(DOCUMENT_PORTAIL, 'Document : 1 à 15 chiffres.')

/** Phase d'une écriture : le dépôt du fichier, ou la liaison du document à son opération. */
export const PHASES_ECRITURE = ['depot', 'liaison'] as const
export type PhaseEcriture = (typeof PHASES_ECRITURE)[number]

/** Échec certain d'une écriture : refusée par le portail (4xx), ou session expirée (401). */
export const MOTIFS_ECHEC_ECRITURE = ['refuse_par_le_portail', 'session'] as const
export type MotifEchecEcriture = (typeof MOTIFS_ECHEC_ECRITURE)[number]

/**
 * Pourquoi l'agent renonce à une écriture : opération relue qui ne s'y prête plus (changée,
 * justifiée, introuvable, ventilée, provisoire), document lié à une autre opération, fichier qui
 * n'est pas celui réservé ou pas reçu, page sous service worker ou témoin absent, écriture coupée.
 */
export const MOTIFS_ABANDON_JUSTIFICATIF = [
  'operation_changee',
  'operation_justifiee',
  'operation_introuvable',
  'ventilee',
  'provisoire',
  'document_lie_ailleurs',
  'empreinte',
  'fichier_non_recu',
  'service_worker',
  'temoin_absent',
  'ecriture_coupee',
] as const
export type MotifAbandonJustificatif = (typeof MOTIFS_ABANDON_JUSTIFICATIF)[number]

/**
 * Un compte rendu de l'agent sur une ligne du registre : déposé (le document créé), demande du
 * feu vert de la liaison, demande du document à vérifier (jamais un feu vert), relié (fait, ou
 * constaté sur le document relu), échec certain, issue inconnue, abandon. Union stricte : rien
 * d'autre ne passe.
 */
export const etapeJustificatif = z.discriminatedUnion('etape', [
  z.object({ etape: z.literal('depose'), document: documentPortail }).strict(),
  z.object({ etape: z.literal('relier') }).strict(),
  z.object({ etape: z.literal('verifier') }).strict(),
  z
    .object({
      etape: z.literal('relie'),
      document: documentPortail,
      constate: z.literal(true).optional(),
    })
    .strict(),
  z
    .object({
      etape: z.literal('echec'),
      phase: z.enum(PHASES_ECRITURE),
      motif: z.enum(MOTIFS_ECHEC_ECRITURE),
      statut: z.int().min(400).max(499).optional(),
    })
    .strict(),
  z.object({ etape: z.literal('incertain'), phase: z.enum(PHASES_ECRITURE) }).strict(),
  z.object({ etape: z.literal('abandon'), motif: z.enum(MOTIFS_ABANDON_JUSTIFICATIF) }).strict(),
])
export type EtapeJustificatif = z.infer<typeof etapeJustificatif>

/** Refus d'un compte rendu, en 200 : écriture coupée, étape impossible depuis la ligne, autre document. */
export const REFUS_ETAPE_JUSTIFICATIF = [
  'ecriture_coupee',
  'etape_impossible',
  'document_different',
] as const
export type RefusEtapeJustificatif = (typeof REFUS_ETAPE_JUSTIFICATIF)[number]

/** La réponse du serveur à un compte rendu : feu vert de la liaison et son document, ou un refus. */
export const reponseEtapeJustificatif = z.discriminatedUnion('ok', [
  z
    .object({
      ok: z.literal(true),
      relier: z.boolean().optional(),
      document: documentPortail.optional(),
    })
    .strict(),
  z.object({ ok: z.literal(false), refus: z.enum(REFUS_ETAPE_JUSTIFICATIF) }).strict(),
])
export type ReponseEtapeJustificatif = z.infer<typeof reponseEtapeJustificatif>

/** Ce que l'agent a vu d'une écriture envoyée. */
export type IssueEcriture = {
  /** Statut HTTP ; null : aucune réponse (réseau, délai). */
  statut: number | null
  /** Le document créé (dépôt) ou relié (liaison), relu et vérifié par l'adaptateur ; sinon null. */
  document: string | null
  /** La page et le réseau ont vu la même issue. */
  accord: boolean
}

/**
 * Le compte rendu d'une écriture : faite (2xx avec un document lisible), session expirée (401),
 * refusée par le portail (autre 4xx, avec son statut) ; sinon incertaine (réseau, délai, 5xx, 2xx
 * sans document lisible, page et réseau en désaccord). Jamais un nouvel essai d'office : seule
 * l'issue incertaine dit qu'elle manque.
 */
export function etapeApresEcriture(phase: PhaseEcriture, i: IssueEcriture): EtapeJustificatif {
  const incertain: EtapeJustificatif = { etape: 'incertain', phase }
  if (!i.accord || i.statut === null || !Number.isInteger(i.statut)) return incertain
  if (i.statut >= 200 && i.statut < 300) {
    if (i.document === null || !DOCUMENT_PORTAIL.test(i.document)) return incertain
    return phase === 'depot'
      ? { etape: 'depose', document: i.document }
      : { etape: 'relie', document: i.document }
  }
  if (i.statut === 401) return { etape: 'echec', phase, motif: 'session', statut: 401 }
  if (i.statut >= 400 && i.statut < 500)
    return { etape: 'echec', phase, motif: 'refuse_par_le_portail', statut: i.statut }
  return incertain
}

/**
 * Statuts d'un refus certain du fichier par le portail (trop gros, type refusé, illisible) : la
 * pièce se dépose désormais à la main, au lieu de revenir en tête à chaque passage.
 */
export const STATUTS_FICHIER_REFUSE = [413, 415, 422] as const

/** La ligne qu'un compte rendu vise : celle de cette prise et de cet ordinateur. */
export type LigneEtapeJustificatif = {
  statut: StatutJustificatif
  /** Le fichier a été remis à l'agent. */
  remis: boolean
  documentRef: string | null
  tentativesLiaison: number
  /**
   * Un échec de liaison est déjà compté pour cette prise : un seul par prise (l'agent arrête
   * toutes ses écritures au premier refus), un second compte rendu est une répétition. Absent :
   * aucun.
   */
  echecLiaisonCompte?: boolean
}

export type SuiteEtapeJustificatif =
  /** Transitions du registre, dans l'ordre ; `manuelle` : la pièce se dépose désormais à la main. */
  | { suite: 'transition'; evenements: EvenementJustificatif[]; manuelle: boolean }
  /** Feu vert de la liaison (le serveur recalcule aussi le mode) : le document à relier. */
  | { suite: 'relier'; document: string }
  /** Vérification : le document à relire et à constater, jamais à relier. */
  | { suite: 'constat'; document: string }
  /** Rien ne change : compte rendu déjà tenu, ou ligne laissée déposée et revue au passage suivant. */
  | { suite: 'inchangee'; raison: 'deja_fait' | 'a_verifier' }
  | { suite: 'refus'; refus: Exclude<RefusEtapeJustificatif, 'ecriture_coupee'> }

/** Après le dépôt, la liaison n'est plus possible : le document reste seul chez le portail. */
const ABANDONS_ORPHELINS: readonly MotifAbandonJustificatif[] = [
  'document_lie_ailleurs',
  'operation_changee',
  'operation_justifiee',
  'operation_introuvable',
  'ventilee',
  'provisoire',
]
/** Après le dépôt, une cause passagère : la liaison se retente au passage suivant. */
const ABANDONS_PASSAGERS: readonly MotifAbandonJustificatif[] = [
  'service_worker',
  'temoin_absent',
  'ecriture_coupee',
]

/**
 * Ce qu'un compte rendu de l'agent fait au registre, d'après la ligne qu'il vise :
 * - déposé : depuis la ligne remise, ou incertaine (dépôt tardif) ;
 * - relié : depuis la ligne déposée, du même document ;
 * - échec du dépôt : refus du portail, compté (fichier refusé : la pièce passe à la main) ; session
 *   expirée, libérée sans compter d'essai ;
 * - échec de la liaison : compté une fois par prise (répété, déjà fait), orpheline au
 *   `ESSAIS_LIAISON_JUSTIFICATIF`-ième ; session, ligne laissée déposée ;
 * - issue inconnue : dépôt incertain (un humain vérifie chez le cabinet) ; liaison laissée déposée,
 *   revue au passage suivant ;
 * - abandon : avant le dépôt, libérée ; après, orpheline si la liaison n'est plus possible, sinon
 *   laissée déposée.
 * Un compte rendu déjà tenu ne change rien (même réponse) ; un autre document est refusé.
 */
export function suiteEtapeJustificatif(
  l: LigneEtapeJustificatif,
  e: EtapeJustificatif,
): SuiteEtapeJustificatif {
  const agent = (type: TypeEvenementJustificatif): EvenementJustificatif => ({ type, par: 'agent' })
  const transition = (
    manuelle: boolean,
    ...evenements: EvenementJustificatif[]
  ): SuiteEtapeJustificatif => ({ suite: 'transition', evenements, manuelle })
  const dejaFait: SuiteEtapeJustificatif = { suite: 'inchangee', raison: 'deja_fait' }
  const aVerifier: SuiteEtapeJustificatif = { suite: 'inchangee', raison: 'a_verifier' }
  const impossible: SuiteEtapeJustificatif = { suite: 'refus', refus: 'etape_impossible' }
  const autreDocument: SuiteEtapeJustificatif = { suite: 'refus', refus: 'document_different' }
  const memeDocument = (document: string) => (l.documentRef === document ? dejaFait : autreDocument)
  const remise = l.statut === 'reserve' && l.remis
  const deposee = l.statut === 'depose' && l.documentRef !== null
  switch (e.etape) {
    case 'depose':
      if (remise || l.statut === 'incertain') return transition(false, agent('depose'))
      return l.statut === 'depose' || l.statut === 'relie' || l.statut === 'orphelin'
        ? memeDocument(e.document)
        : impossible
    case 'relier':
      return deposee ? { suite: 'relier', document: l.documentRef! } : impossible
    case 'verifier':
      return deposee ? { suite: 'constat', document: l.documentRef! } : impossible
    case 'relie':
      if (deposee)
        return l.documentRef === e.document ? transition(false, agent('relie')) : autreDocument
      return l.statut === 'relie' ? memeDocument(e.document) : impossible
    case 'echec':
      if (e.phase === 'depot') {
        if (remise) {
          if (e.motif === 'session') return transition(false, agent('abandonner'))
          const refuse =
            e.statut !== undefined &&
            (STATUTS_FICHIER_REFUSE as readonly number[]).includes(e.statut)
          return transition(refuse, agent('echec'))
        }
        return l.statut === 'echec' || l.statut === 'annule' ? dejaFait : impossible
      }
      if (deposee) {
        if (e.motif === 'session') return aVerifier
        if (l.echecLiaisonCompte) return dejaFait
        return l.tentativesLiaison + 1 >= ESSAIS_LIAISON_JUSTIFICATIF
          ? transition(false, agent('liaison_echouee'), { type: 'orpheliner', par: 'serveur' })
          : transition(false, agent('liaison_echouee'))
      }
      return l.statut === 'orphelin' ? dejaFait : impossible
    case 'incertain':
      if (e.phase === 'depot') {
        if (l.statut === 'reserve')
          return l.remis ? transition(false, agent('incertain')) : impossible
        return ['incertain', 'depose', 'relie', 'orphelin'].includes(l.statut)
          ? dejaFait
          : impossible
      }
      if (deposee) return aVerifier
      return l.statut === 'relie' ? dejaFait : impossible
    case 'abandon':
      if (l.statut === 'reserve')
        return e.motif === 'document_lie_ailleurs'
          ? impossible
          : transition(false, agent('abandonner'))
      if (deposee) {
        if (ABANDONS_ORPHELINS.includes(e.motif)) return transition(false, agent('orpheliner'))
        return ABANDONS_PASSAGERS.includes(e.motif) ? aVerifier : impossible
      }
      if (l.statut === 'annule') return e.motif === 'document_lie_ailleurs' ? impossible : dejaFait
      return l.statut === 'orphelin' && ABANDONS_ORPHELINS.includes(e.motif) ? dejaFait : impossible
  }
}

/** Le titulaire ouvre (vrai) ou coupe (faux) l'écriture d'une connexion, dans l'appli. */
export const reglageEcriture = z.object({ ouvrir: z.boolean() }).strict()
export type ReglageEcriture = z.infer<typeof reglageEcriture>

/** Pourquoi l'agent n'écrit pas, à la relecture de l'opération juste avant d'écrire. */
export const REFUS_AVANT_ECRITURE = [
  'operation_introuvable',
  'operation_changee',
  'operation_justifiee',
  'ventilee',
  'provisoire',
] as const satisfies readonly MotifAbandonJustificatif[]
export type RefusAvantEcriture = (typeof REFUS_AVANT_ECRITURE)[number]

/**
 * L'opération relue juste avant d'écrire se prête-t-elle encore à l'action du plan ? Dans cet
 * ordre : introuvable (ou une autre), changée (jour, montant ou devise), déjà justifiée chez la
 * source (une liaison qui remplace la liste ne vise qu'une opération sans aucun justificatif),
 * ventilée, provisoire ; sinon null.
 */
export function refusAvantEcriture(
  relue: OperationPortail | null,
  attendue: Pick<ActionPlanJustificatif, 'operation' | 'date' | 'montantCts' | 'devise'>,
): RefusAvantEcriture | null {
  if (relue === null || relue.ref !== attendue.operation) return 'operation_introuvable'
  if (
    relue.date !== attendue.date ||
    relue.montantCts !== attendue.montantCts ||
    relue.devise !== attendue.devise
  )
    return 'operation_changee'
  if (relue.justificatifs > 0) return 'operation_justifiee'
  if (relue.ventilee) return 'ventilee'
  if (relue.provisoire) return 'provisoire'
  return null
}

/** Le premier verrou qui manque pour ouvrir l'écriture d'une connexion. */
export const MOTIFS_OUVERTURE_ECRITURE = [
  'via_assistant',
  'pas_titulaire',
  'ecritures_coupees',
  'portail_sans_ecriture',
  'connexion_inactive',
  'entite',
  'entite_hors_depot',
  'passage_non_valide',
  'autre_version',
  'agent_ancien',
] as const
export type MotifOuvertureEcriture = (typeof MOTIFS_OUVERTURE_ECRITURE)[number]

export const LIBELLES_OUVERTURE_ECRITURE: Readonly<Record<MotifOuvertureEcriture, string>> = {
  via_assistant: 'L’écriture s’ouvre dans l’appli, jamais via l’assistant.',
  pas_titulaire: 'Seul le titulaire de la connexion ouvre l’écriture.',
  ecritures_coupees: 'Écritures coupées par la configuration (portails.ecritures).',
  portail_sans_ecriture:
    'Ce portail n’a pas encore d’écriture déclarée (dépôt et liaison) : l’écriture ne s’y ouvre pas.',
  connexion_inactive:
    'Connexion désactivée : la réactiver, puis valider un nouveau passage à blanc.',
  entite: 'Entité sans SIREN, privée ou archivée.',
  entite_hors_depot: 'L’entité n’est pas en dépôt direct.',
  passage_non_valide:
    'Passage à blanc pas encore validé depuis la dernière coupure de l’écriture : en valider un.',
  autre_version: 'Passage à blanc validé pour une autre version de la règle : à revalider.',
  agent_ancien: `Agent du Mac de la connexion trop ancien ou non relié : le mettre à jour (${VERSION_ECRITURE_AGENT} au moins) avant d’ouvrir l’écriture.`,
}

/** Ce que l'ouverture de l'écriture vérifie, dans l'ordre. */
export type VerrousOuvertureEcriture = {
  /** Demandé via l'assistant (MCP, assistant des fiches) : jamais. */
  viaAssistant: boolean
  /** L'acteur est le titulaire de la connexion. */
  titulaire: boolean
  /** Coupe-circuit de déploiement `portails.ecritures` : fermé, rien ne s'ouvre. */
  ecrituresPermises: boolean
  /** Le fournisseur de la connexion déclare le dépôt et la liaison (`portailQuiEcrit`). */
  portailEcrit: boolean
  /** La connexion est active. */
  active: boolean
  /** Son entité se prête au rapprochement : ni privée, ni archivée, avec un SIREN. */
  entitePropre: boolean
  /** Son entité est en dépôt direct chez le fournisseur de la connexion. */
  entiteEnDepot: boolean
  /** Dernière validation d'un passage à blanc par le titulaire, et sa version de la règle. */
  passageValideLe: Date | null
  versionValidee: number | null
  /** Dernière coupure de l'écriture (titulaire changé, connexion désactivée, coupure). */
  ecritureCoupeeLe: Date | null
  /** Version de l'agent du Mac lié à la connexion ; aucun agent, ou version inconnue : null. */
  versionAgent: string | null
}

/**
 * La lecture (tâches « à trancher » et « à fournir », recherche dans les mails) suit la validation
 * du passage à blanc par le titulaire actuel : une coupure de l'écriture ne la fait pas tomber
 * (amendement 9 : écriture coupée, la lecture et le bilan continuent) ; un changement de titulaire,
 * si (décision du 13/10/2026). L'écriture, elle, suit `validationEnVigueur`.
 */
export function validationPourLaLecture(v: {
  passageValideLe: Date | null
  passageValideParId: string | null
  titulaireId: string
}): boolean {
  return v.passageValideLe !== null && v.passageValideParId === v.titulaireId
}

/** La validation du passage à blanc vaut encore : posée, et pas de coupure de l'écriture depuis. */
export function validationEnVigueur(v: {
  passageValideLe: Date | null
  ecritureCoupeeLe: Date | null
}): boolean {
  return (
    v.passageValideLe !== null &&
    (v.ecritureCoupeeLe === null || v.ecritureCoupeeLe.getTime() < v.passageValideLe.getTime())
  )
}

/**
 * L'écriture d'une connexion ne s'ouvre que par son titulaire, dans l'appli, `portails.ecritures`
 * posé, chez un portail qui déclare ses écritures (`portailQuiEcrit`), sur une connexion active
 * dont l'entité se prête au dépôt direct, avec une validation du
 * passage à blanc postérieure à la dernière coupure et à la version courante de la règle, et un
 * agent du Mac qui sait écrire (`VERSION_ECRITURE_AGENT`). Sinon le premier verrou qui manque.
 */
export function motifOuvertureEcriture(v: VerrousOuvertureEcriture): MotifOuvertureEcriture | null {
  if (v.viaAssistant) return 'via_assistant'
  if (!v.titulaire) return 'pas_titulaire'
  if (!v.ecrituresPermises) return 'ecritures_coupees'
  if (!v.portailEcrit) return 'portail_sans_ecriture'
  if (!v.active) return 'connexion_inactive'
  if (!v.entitePropre) return 'entite'
  if (!v.entiteEnDepot) return 'entite_hors_depot'
  if (!validationEnVigueur(v)) return 'passage_non_valide'
  if (v.versionValidee !== VERSION_REGLE_JUSTIFICATIFS) return 'autre_version'
  if (v.versionAgent === null || !versionSuffisante(v.versionAgent, VERSION_ECRITURE_AGENT))
    return 'agent_ancien'
  return null
}

/** Pourquoi une personne ne coupe pas l'écriture. */
export const MOTIFS_COUPURE_ECRITURE = ['via_assistant', 'non_autorise'] as const
export type MotifCoupureEcriture = (typeof MOTIFS_COUPURE_ECRITURE)[number]

export const LIBELLES_COUPURE_ECRITURE: Readonly<Record<MotifCoupureEcriture, string>> = {
  via_assistant: 'L’écriture se coupe dans l’appli, jamais via l’assistant.',
  non_autorise: 'Seuls le titulaire de la connexion et un administrateur coupent l’écriture.',
}

/**
 * Couper l'écriture n'écrit rien chez le cabinet : toujours permis au titulaire de la connexion et
 * à un administrateur, dans l'appli ; jamais via l'assistant ni à une autre personne.
 */
export function motifCoupureEcriture(v: {
  viaAssistant: boolean
  titulaire: boolean
  administrateur: boolean
}): MotifCoupureEcriture | null {
  if (v.viaAssistant) return 'via_assistant'
  return v.titulaire || v.administrateur ? null : 'non_autorise'
}

/** Une ligne vivante du registre d'une connexion, telle que la vérification la lit. */
export type LigneRegistreConnexion = {
  id: string
  statut: StatutJustificatif
  operationRef: string | null
  pieceId: string
}

/**
 * Les actions d'un passage en écriture : d'abord une action pour chaque opération lue qui porte
 * une ligne `depose` de l'appli (document déposé, pas encore relié : liaison échouée, incertaine
 * ou interrompue) ; puis les actions du plan, hors des opérations ainsi tenues. Pour une ligne
 * déposée, `relier` seulement si la règle de ce passage dit l'opération sûre sur la pièce de la
 * ligne (le plan la relie : état `sure`, même pièce, action `relier`) ; sinon (montant corrigé,
 * pièce rejetée, jumelle arrivée, autre pièce désignée, opération justifiée chez la source depuis)
 * `verifier` : l'agent relit le document et constate, jamais il ne relie (CLAUDE.md : l'agent
 * n'exécute jamais une proposition). Chaque groupe par jour puis référence,
 * `MAX_ECRITURES_PAR_PASSAGE` au plus en tout. Aucune à blanc, ni sans relecture confirmée.
 */
export function actionsDeVerification(e: {
  lignes: readonly LigneRegistreConnexion[]
  operations: ReadonlyMap<string, Pick<OperationAJustifier, 'date' | 'montantCts' | 'devise'>>
  /** L'état que le plan de ce passage donne à chaque opération (`planDesJustificatifs`). */
  planifiees: ReadonlyMap<string, Pick<OperationPlanifiee, 'etat' | 'pieceId' | 'action'>>
  /** Les actions du plan (`planDesJustificatifs`). */
  plan: readonly ActionPlanJustificatif[]
  mode: ModeJustificatifs
  relecture: RelectureJustificatifs
  /**
   * La catégorie du passage permet un dépôt (`depotPermisParCategorie`) ; faux : aucune action
   * de dépôt (les liaisons et les vérifications restent). Absent : permis.
   */
  depotPermis?: boolean
  maxActions?: number
}): ActionRendueJustificatif[] {
  if (e.mode !== 'ecriture' || e.relecture !== 'ok') return []
  const plafond = Math.max(
    0,
    Math.min(Math.floor(e.maxActions ?? MAX_ECRITURES_PAR_PASSAGE), MAX_ECRITURES_PAR_PASSAGE),
  )
  const tenues = new Set<string>()
  const verifications: ActionRendueJustificatif[] = []
  for (const l of e.lignes) {
    if (l.statut !== 'depose' || l.operationRef === null || tenues.has(l.operationRef)) continue
    const op = e.operations.get(l.operationRef)
    if (!op) continue
    tenues.add(l.operationRef)
    const p = e.planifiees.get(l.operationRef)
    const sure = p?.etat === 'sure' && p.pieceId === l.pieceId && p.action === 'relier'
    verifications.push({
      operation: l.operationRef,
      action: sure ? 'relier' : 'verifier',
      date: op.date,
      montantCts: op.montantCts,
      devise: op.devise,
      justificatifId: l.id,
    })
  }
  verifications.sort((a, b) => comparer(a.date, b.date) || comparer(a.operation, b.operation))
  const duPlan = e.plan.filter(
    (a) =>
      !tenues.has(a.operation) && (e.depotPermis !== false || a.action !== 'deposer_et_relier'),
  )
  return [...verifications, ...duPlan].slice(0, plafond)
}

/**
 * Pourquoi le serveur ne donne pas le feu vert d'une liaison : écriture coupée (mode recalculé à
 * blanc) ; la règle de ce passage ne dit plus l'opération sûre sur la pièce de la ligne, ou une
 * action du plan qui ne la relie pas ; pièce sortie de la frontière, ou plus déposée par cette
 * ligne.
 */
export const REFUS_LIAISON_JUSTIFICATIF = [
  'ecriture_coupee',
  'hors_plan',
  'piece_indisponible',
] as const
export type RefusLiaisonJustificatif = (typeof REFUS_LIAISON_JUSTIFICATIF)[number]

/** Ce que le feu vert d'une liaison relit, au moment de relier. */
export type VerrousLiaison = {
  /** Mode recalculé au feu vert (un verrou tombé en cours de passage arrête tout). */
  mode: ModeJustificatifs
  /** La ligne déposée : son identifiant, sa pièce, et si ce passage l'a réservée. */
  ligne: { id: string; pieceId: string; deCePassage: boolean }
  /** L'action rendue par ce passage sur l'opération de la ligne ; null : absente. */
  actionDuPlan: Pick<ActionRendueJustificatif, 'action' | 'justificatifId'> | null
  /** L'opération telle que le plan de ce passage l'a écrite ; null : pas lue par ce passage. */
  operation:
    | (Pick<OperationPlanifiee, 'etat' | 'pieceId' | 'action'> & {
        /** Écartée par un humain depuis, ou la pièce exclue (« Ce n'est pas cette pièce »). */
        ecartee: boolean
        pieceExclue: boolean
      })
    | null
  /** État de dépôt de la pièce de la ligne, frontière rejouée ; null : hors de la frontière. */
  etatPiece: EtatDepot | null
}

/**
 * Le feu vert d'une liaison, dans cet ordre : écriture recalculée (sinon `ecriture_coupee`) ;
 * l'action rendue par ce passage relie cette ligne (déposer puis relier, pour la ligne qu'il a
 * réservée ; relier, pour une ligne déjà déposée ; jamais une vérification), et la règle de ce
 * passage dit encore l'opération sûre sur la pièce de la ligne, avec cette action, ni écartée ni
 * sa pièce exclue par un humain depuis (sinon `hors_plan`) ; la pièce encore dans la frontière,
 * déposée par cette ligne (sinon `piece_indisponible`). Une liaison n'est jamais l'exécution
 * d'une proposition.
 */
export function refusLiaison(v: VerrousLiaison): RefusLiaisonJustificatif | null {
  if (v.mode !== 'ecriture') return 'ecriture_coupee'
  const a = v.actionDuPlan
  const op = v.operation
  if (a === null || op === null) return 'hors_plan'
  const relieCetteLigne =
    a.action === 'relier'
      ? a.justificatifId === v.ligne.id
      : a.action === 'deposer_et_relier' && v.ligne.deCePassage
  if (
    !relieCetteLigne ||
    op.etat !== 'sure' ||
    op.pieceId !== v.ligne.pieceId ||
    op.action !== a.action ||
    op.ecartee ||
    op.pieceExclue
  )
    return 'hors_plan'
  const p = v.etatPiece
  return p?.etat === 'deposee' && p.justificatifId === v.ligne.id ? null : 'piece_indisponible'
}

/**
 * Pourquoi l'agent arrête toutes les écritures d'un passage (noté `ecritures_arretees`, la lecture
 * et le bilan continuent) : écriture non armée coupée, prise perdue, session expirée, écriture
 * coupée, issue incertaine, refus du portail, service worker, témoin absent, page de départ légère
 * non servie (repli sur l'appli), relecture impossible.
 */
export const MOTIFS_ARRET_ECRITURES = [
  'violation',
  'prise_perdue',
  'session',
  'ecriture_coupee',
  'incertain',
  'refus_portail',
  'service_worker',
  'temoin_absent',
  'depart_repli',
  'relecture',
] as const
export type MotifArretEcritures = (typeof MOTIFS_ARRET_ECRITURES)[number]

// ---------------------------------------------------------------------------------------------
// Conseils d'échec et titres des tâches
// ---------------------------------------------------------------------------------------------

/**
 * Motifs d'échec qu'un conseil explique (en plus des classes d'erreur de collecte) : extension,
 * dossier par le SIREN, écriture coupée, dépôt interrompu, et chaque arrêt des écritures.
 */
export const MOTIFS_ECHEC_JUSTIFICATIFS = [
  'extension',
  ...REFUS_DOSSIER_JUSTIFICATIFS,
  'ecriture_coupee',
  'depot_interrompu',
  'violation',
  'prise_perdue',
  'session',
  'incertain',
  'refus_portail',
  'service_worker',
  'temoin_absent',
  'depart_repli',
  'relecture',
] as const
export type MotifEchecJustificatifs = (typeof MOTIFS_ECHEC_JUSTIFICATIFS)[number]

const SESSION =
  'Se connecter au portail dans Chrome sur le Mac (session du titulaire), puis « C’est parti ».'

const CONSEILS_MOTIF: Readonly<Record<MotifEchecJustificatifs, string>> = {
  extension:
    'Ouvrir Chrome sur le Mac, extension Admin PMVB reliée et à jour, puis « C’est parti ».',
  aucun:
    'Aucun dossier du portail ne porte le SIREN de l’entité : vérifier le SIREN de sa fiche, et que le compte du titulaire voit ce dossier.',
  plusieurs:
    'Plusieurs dossiers du portail portent le SIREN de l’entité : rien n’est lu tant qu’un seul ne correspond pas.',
  change:
    'Le dossier du portail n’est plus celui retenu au premier passage : rien n’est lu, vérifier la connexion.',
  entite: 'Entité sans SIREN, privée ou archivée : compléter sa fiche avant le rapprochement.',
  ecriture_coupee:
    'Écriture coupée pendant le passage : rien d’autre n’a été écrit, la suite attend le passage suivant.',
  depot_interrompu:
    'Dépôt interrompu : vérifier chez le cabinet s’il est arrivé avant tout nouveau dépôt (décision ouverte sur la pièce).',
  violation:
    'Écriture non armée coupée : passage bloqué, rien d’autre n’a été écrit ; l’adaptateur est à revoir avant le passage suivant.',
  prise_perdue:
    'Passage repris ailleurs ou échu pendant les écritures : elles se sont arrêtées, la suite attend le passage suivant.',
  session: SESSION,
  incertain:
    'Issue d’un dépôt ou d’une liaison inconnue : vérifier chez le cabinet avant tout nouveau dépôt (décision ouverte sur la pièce, ou liaison revue au passage suivant).',
  refus_portail:
    'Le portail a refusé un dépôt ou une liaison : écritures arrêtées pour ce passage ; un fichier refusé se dépose à la main.',
  service_worker:
    'Un service worker contrôle la page du portail : aucune écriture ce passage, la lecture continue.',
  temoin_absent:
    'Le témoin des requêtes n’a pas vu la dernière lecture : aucune écriture ce passage, la lecture continue.',
  depart_repli:
    'Page de départ légère indisponible, repli sur l’appli du portail : aucune écriture ce passage, la lecture continue.',
  relecture:
    'Relecture de l’opération impossible : aucune écriture ce passage, la lecture continue.',
}

const CONSEILS_CLASSE: Partial<Record<ClasseErreurCollecte, string>> = {
  auth_requise: SESSION,
  mfa_requis: SESSION,
  captcha_requis: SESSION,
  consentement_requis: SESSION,
  session_expiree: SESSION,
  politique_reseau: 'Requête non déclarée coupée : passage arrêté, l’adaptateur est à revoir.',
  agent_indisponible:
    'Agent du Mac injoignable : allumer le Mac et vérifier que l’agent Admin PMVB tourne.',
}

/**
 * Texte clair pour l'écran Portails : le motif d'abord (extension, dossier par le SIREN, écriture
 * coupée, dépôt interrompu), sinon la classe d'erreur (session, requête coupée, agent) ; rien de
 * connu : null.
 */
export function conseilEchecJustificatifs(
  classe: ClasseErreurCollecte | null,
  motif: string | null,
): string | null {
  if (motif !== null && Object.hasOwn(CONSEILS_MOTIF, motif))
    return CONSEILS_MOTIF[motif as MotifEchecJustificatifs]
  if (classe !== null && Object.hasOwn(CONSEILS_CLASSE, classe))
    return CONSEILS_CLASSE[classe] ?? null
  return null
}

/**
 * Actions des tâches de justificatifs d'avant le 02/11/2026 (à choisir, à fournir, dépôts à
 * vérifier, une par connexion et par action) : la tâche du collecteur les remplace
 * (`ACTION_TACHE_COLLECTEUR`, `tacheCollecteur`, packages/core/src/collecteurs.ts). Gardées pour
 * les fermer et pour la fiche des anciennes tâches, qui mène au bilan.
 */
export const ACTIONS_TACHES_JUSTIFICATIFS = [
  'justificatifs_a_trancher',
  'justificatifs_a_fournir',
  'justificatifs_a_verifier',
] as const

// ---------------------------------------------------------------------------------------------
// Recherche des pièces manquantes dans les mails (décision du 12/10/2026)
// ---------------------------------------------------------------------------------------------
// Le job cherche dans les mails les pièces des opérations à fournir d'une connexion (le port
// `ChercheurPiecesMails` de recherche-pieces.ts), au nom de son titulaire, une fois son passage
// à blanc validé, une fois par jour (Paris) au plus. Ici, les verrous, le choix des opérations,
// ce qu'une réponse écrit sur chacune et les comptes gardés au journal de la connexion.

/** Opérations à fournir confiées à la recherche du jour d'une connexion, au plus. */
export const OPERATIONS_PAR_RECHERCHE_MAILS = 20
/** Une opération déjà cherchée ne l'est de nouveau qu'au-delà de ce délai, en jours. */
export const JOURS_AVANT_NOUVELLE_RECHERCHE = 7
/** Pièces retrouvées gardées par opération. */
export const MAX_PIECES_MAILS = 20
/** Motif d'une recherche qui n'a trouvé aucune boîte à lire, en signes. */
export const LONGUEUR_MOTIF_RECHERCHE = 300
/**
 * Action du journal de la connexion (objet `connexion_portail`) pour chaque recherche du jour :
 * des nombres, jamais une donnée (`EntreeRechercheMails`). La marque posée au lancement est lue
 * avant d'en lancer une autre ; les comptes suivent à la fin.
 */
export const ACTION_RECHERCHE_MAILS = 'recherche_mails_justificatifs'

/** Pourquoi la recherche n'agit pas pour une connexion : le premier verrou qui manque. */
export const MOTIFS_SANS_RECHERCHE_MAILS = [
  'connexion_inactive',
  'entite',
  'passage_non_valide',
  'sans_titulaire',
  'deja_ce_jour',
] as const
export type MotifSansRechercheMails = (typeof MOTIFS_SANS_RECHERCHE_MAILS)[number]

/** Ce que la recherche vérifie pour une connexion, dans l'ordre. */
export type VerrousRechercheMails = {
  /** Connexion active (son fournisseur n'est pas coupé : le job ne lit que ceux-là). */
  active: boolean
  /**
   * Son entité se prête au rapprochement : ni privée (la recherche ne traverse jamais la cloison
   * d'un espace privé), ni archivée, avec un SIREN.
   */
  entitePropre: boolean
  /**
   * Son passage à blanc a été validé (`passage_valide_le` posé) par son titulaire actuel : après un
   * changement de titulaire, rien n'est cherché au nom du nouveau avant qu'il ait validé.
   */
  passageValide: boolean
  /**
   * Son titulaire est une personne, jamais le compte assistant (qui lit tout), qui lit l'entité par
   * ses rôles, sans accès temporaire : la recherche lit en son nom les seules boîtes qu'il voit.
   */
  titulaire: boolean
  /** Une recherche a déjà été lancée pour elle ce jour (Paris), d'après son journal. */
  faiteCeJour: boolean
}

/**
 * La recherche agit pour la connexion si tous les verrous sont ouverts ; sinon le premier qui
 * manque : connexion inactive, entité qui ne s'y prête pas, passage à blanc pas encore validé,
 * pas de titulaire au nom de qui chercher, recherche déjà lancée ce jour.
 */
export function verrouRechercheMails(v: VerrousRechercheMails): MotifSansRechercheMails | null {
  if (!v.active) return 'connexion_inactive'
  if (!v.entitePropre) return 'entite'
  if (!v.passageValide) return 'passage_non_valide'
  if (!v.titulaire) return 'sans_titulaire'
  if (v.faiteCeJour) return 'deja_ce_jour'
  return null
}

/** Ce que le choix des opérations lit d'une opération de la connexion. */
export type OperationARechercher = {
  /** Référence chez le portail : unique pour la connexion. */
  ref: string
  /** Jour de l'opération (AAAA-MM-JJ). */
  date: string
  etat: EtatOperationPortail
  /** Dernière recherche menée à son terme (trouvée, rien, aucune boîte) ; null : jamais. */
  rechercheLe: Date | null
}

/** Jamais cherchée, ou cherchée il y a plus de sept jours : à chercher de nouveau. */
export function rechercheMailsDue(rechercheLe: Date | null, maintenant: Date): boolean {
  return (
    rechercheLe === null ||
    maintenant.getTime() - rechercheLe.getTime() > JOURS_AVANT_NOUVELLE_RECHERCHE * 86_400_000
  )
}

const comparerTextes = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0)

/** Rang du jour de Paris depuis le 1er janvier 1970 : le départ de la file tournante. */
const rangDuJour = (maintenant: Date) =>
  Math.round(Date.parse(`${jourAParis(maintenant)}T00:00:00Z`) / 86_400_000)

/**
 * Les opérations confiées à la recherche du jour : à fournir, jamais cherchées ou cherchées il y
 * a plus de sept jours, rangées par date puis référence (l'ordre ne dépend pas de la lecture),
 * vingt au plus. Au-delà de vingt, une file tournante : la liste part chaque jour (Paris) vingt
 * rangs plus loin, de sorte qu'en un jour par tranche de vingt, chacune passe à son tour. Une
 * recherche coupée ne pose pas de date : l'opération reste dans la file et revient le lendemain,
 * sans jamais garder la place des autres (vingt opérations toujours coupées, trop de mails d'un
 * même expéditeur par exemple, ne bloquent pas les suivantes).
 */
export function operationsARechercher<T extends OperationARechercher>(
  ops: readonly T[],
  maintenant: Date,
): T[] {
  const dues = ops
    .filter((op) => op.etat === 'a_fournir' && rechercheMailsDue(op.rechercheLe, maintenant))
    .sort((a, b) => comparerTextes(a.date, b.date) || comparerTextes(a.ref, b.ref))
  if (dues.length <= OPERATIONS_PAR_RECHERCHE_MAILS) return dues
  const depart = (rangDuJour(maintenant) * OPERATIONS_PAR_RECHERCHE_MAILS) % dues.length
  return [...dues.slice(depart), ...dues.slice(0, depart)].slice(0, OPERATIONS_PAR_RECHERCHE_MAILS)
}

/** Statuts d'une pièce rejetée par un humain ou doublon d'une autre : elle n'est plus retrouvée. */
export const STATUTS_PIECE_ECARTEE = [
  'ignoree',
  'doublon',
] as const satisfies readonly StatutPiece[]

/**
 * Les pièces retrouvées d'une opération qui comptent encore. `pieces_mails` est la photo de la
 * dernière recherche : une pièce rejetée depuis par un humain, doublon, rattachée à une autre
 * entité ou absente de `lues` (disparue, ou hors de vue de qui lit) ne retient plus l'opération
 * hors de « à fournir », ni ne la range en « Retrouvée dans les mails ». `lues` : chaque pièce
 * lue, son entité et son statut actuels.
 */
export function piecesMailsValables(
  ids: readonly string[],
  lues: ReadonlyMap<string, { entiteId: string | null; statut: StatutPiece }>,
  entiteId: string,
): string[] {
  return ids.filter((id) => {
    const p = lues.get(id)
    return (
      p !== undefined &&
      p.entiteId === entiteId &&
      !(STATUTS_PIECE_ECARTEE as readonly StatutPiece[]).includes(p.statut)
    )
  })
}

/** Ce qu'une réponse de la recherche écrit sur l'opération cherchée. */
export type SuiteRechercheMails = {
  rechercheLe: Date
  /** Pièces retrouvées : identifiants, sans doublon, `MAX_PIECES_MAILS` au plus. */
  piecesMails: string[]
  /** Pourquoi rien n'a pu être lu (aucune boîte), caviardé et borné ; null sinon. */
  rechercheMotif: string | null
}

const IDENTIFIANT_PIECE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Les pièces retrouvées d'une réponse : identifiants lisibles, sans doublon, vingt au plus. */
function piecesRetrouvees(r: ResultatRechercheMails): string[] {
  return [
    ...new Set(r.pieceIds.filter((x) => typeof x === 'string' && IDENTIFIANT_PIECE.test(x))),
  ].slice(0, MAX_PIECES_MAILS)
}

/**
 * Ce que la réponse de la recherche écrit sur une opération : trouvée, ses pièces et la date ;
 * rien, la date ; aucune boîte, la date et le motif. Coupée, absente de la réponse ou d'une
 * issue inconnue : rien (null), la date ne bouge pas et l'opération est reprise le lendemain.
 * Une pièce retrouvée n'est jamais pour autant sûre ni candidate : seule une pièce rattachée
 * entre dans la règle, au calcul suivant.
 */
export function suiteRechercheMails(
  r: ResultatRechercheMails | undefined,
  maintenant: Date,
): SuiteRechercheMails | null {
  switch (r?.issue) {
    case 'trouvee':
      return { rechercheLe: maintenant, piecesMails: piecesRetrouvees(r), rechercheMotif: null }
    case 'rien':
      return { rechercheLe: maintenant, piecesMails: [], rechercheMotif: null }
    case 'sans_boite':
      return {
        rechercheLe: maintenant,
        piecesMails: [],
        rechercheMotif: [...caviarder(r.motif ?? 'Aucune boîte à lire.', [])]
          .slice(0, LONGUEUR_MOTIF_RECHERCHE)
          .join(''),
      }
    default:
      return null
  }
}

/** Les comptes d'une recherche du jour, gardés au journal de la connexion (des nombres). */
export type ComptesRechercheMails = {
  operations: number
  trouvees: number
  rien: number
  sansBoite: number
  /** Coupées ou absentes de la réponse (recherche en erreur : toutes), reprises le lendemain. */
  coupees: number
  /** Pièces retrouvées, sans doublon. */
  pieces: number
}

/**
 * Les deux entrées d'une recherche du jour au journal de la connexion (`ACTION_RECHERCHE_MAILS`),
 * son jour de Paris et des nombres : `lancee`, la marque posée avant de chercher, sous le verrou
 * de la connexion (le nombre d'opérations confiées), qui tient « une fois par jour » même quand
 * deux tours du job se chevauchent ; `terminee`, les comptes de la recherche, écrits à la fin.
 */
export type EntreeRechercheMails =
  | { jour: string; etape: 'lancee'; operations: number }
  | ({ jour: string; etape: 'terminee' } & ComptesRechercheMails)

/** Les comptes de la recherche des opérations `refs` ; réponses nulles : la recherche a échoué. */
export function comptesRechercheMails(
  refs: readonly string[],
  reponses: ReadonlyMap<string, ResultatRechercheMails> | null,
): ComptesRechercheMails {
  const c: ComptesRechercheMails = {
    operations: refs.length,
    trouvees: 0,
    rien: 0,
    sansBoite: 0,
    coupees: 0,
    pieces: 0,
  }
  const pieces = new Set<string>()
  for (const ref of refs) {
    const r = reponses?.get(ref)
    switch (r?.issue) {
      case 'trouvee':
        c.trouvees++
        for (const p of piecesRetrouvees(r)) pieces.add(p)
        break
      case 'rien':
        c.rien++
        break
      case 'sans_boite':
        c.sansBoite++
        break
      default:
        c.coupees++
    }
  }
  c.pieces = pieces.size
  return c
}

// Recherche demandée par un humain depuis le bilan (« Chercher dans les mails ») : une opération
// à fournir ou à trancher, sans le délai de sept jours du job, mais pas plus d'une recherche par
// opération toutes les dix minutes ; mêmes verrous que la recherche du job (au nom du titulaire,
// une fois son passage à blanc validé), hors celui du jour. La route la met en attente, le worker
// la mène avec les outils des boîtes et écrit son issue comme celle du job (`suiteRechercheMails`).

/** Minutes au moins entre deux recherches d'une même opération demandées depuis le bilan. */
export const MINUTES_ENTRE_RECHERCHES_DEMANDEES = 10
/** États d'une opération qu'un humain fait chercher dans les mails : à fournir, à trancher. */
export const ETATS_RECHERCHE_DEMANDEE = [
  'a_fournir',
  'proposee',
] as const satisfies readonly EtatOperationPortail[]
/**
 * États des opérations qui attendent leur justificatif (onglet « À justifier » du bilan, nombre
 * de chaque connexion dans « Justificatifs ») : à trancher, à fournir (retrouvées dans les mails
 * comprises).
 */
export const ETATS_A_JUSTIFIER = [
  'proposee',
  'a_fournir',
] as const satisfies readonly EtatOperationPortail[]
/** Action du journal de l'opération (objet `operation_portail`) pour chaque demande. */
export const ACTION_DEMANDE_RECHERCHE_MAILS = 'demander_recherche_mails'
/**
 * Action du journal de la connexion pour chaque recherche demandée menée par le worker, à part de
 * `ACTION_RECHERCHE_MAILS` (qui tient « une fois par jour » la recherche du job) : des nombres.
 */
export const ACTION_RECHERCHE_DEMANDEE = 'recherche_mails_demandee'

/** « Chercher dans les mails » sur une opération du bilan. */
export const demandeRechercheMails = z.object({ operationId: z.uuid() }).strict()
export type DemandeRechercheMails = z.infer<typeof demandeRechercheMails>

/** Pourquoi une recherche demandée ne part pas : le premier verrou qui manque. */
export const MOTIFS_REFUS_RECHERCHE_DEMANDEE = [
  'connexion_inactive',
  'entite',
  'passage_non_valide',
  'sans_titulaire',
  'etat',
  'trop_tot',
] as const satisfies readonly (MotifSansRechercheMails | 'etat' | 'trop_tot')[]
export type MotifRefusRechercheDemandee = (typeof MOTIFS_REFUS_RECHERCHE_DEMANDEE)[number]

export const LIBELLES_REFUS_RECHERCHE_DEMANDEE: Readonly<
  Record<MotifRefusRechercheDemandee, string>
> = {
  connexion_inactive: 'Connexion désactivée : aucune recherche dans les mails.',
  entite: 'Entité sans SIREN, privée ou archivée : aucune recherche dans les mails.',
  passage_non_valide:
    'La recherche dans les mails attend un passage à blanc validé par le titulaire de la connexion.',
  sans_titulaire:
    'Aucun titulaire au nom de qui chercher : la recherche lit les boîtes du titulaire de la connexion.',
  etat: 'Seule une opération à fournir ou à trancher se cherche dans les mails.',
  trop_tot: `Déjà cherchée il y a moins de ${MINUTES_ENTRE_RECHERCHES_DEMANDEES} minutes : réessayer plus tard.`,
}

/** Ce qu'une recherche demandée vérifie, dans l'ordre. */
export type VerrousRechercheDemandee = Omit<VerrousRechercheMails, 'faiteCeJour'> & {
  etat: EtatOperationPortail
  /** Dernière demande de recherche pour cette opération ; null : aucune. */
  derniereDemande: Date | null
  /** Dernière recherche menée à son terme (job ou demande) ; null : jamais. */
  derniereRecherche: Date | null
  maintenant: Date
}

/**
 * Une recherche demandée part si les verrous de la recherche du job sont ouverts (hors celui du
 * jour), si l'opération est à fournir ou à trancher, et si ni une demande ni une recherche n'a eu
 * lieu pour elle depuis moins de dix minutes (le délai de sept jours du job ne compte pas) ; sinon
 * le premier verrou qui manque.
 */
export function refusRechercheDemandee(
  v: VerrousRechercheDemandee,
): MotifRefusRechercheDemandee | null {
  const verrou = verrouRechercheMails({ ...v, faiteCeJour: false })
  if (verrou !== null && verrou !== 'deja_ce_jour') return verrou
  if (!(ETATS_RECHERCHE_DEMANDEE as readonly string[]).includes(v.etat)) return 'etat'
  const limite = v.maintenant.getTime() - MINUTES_ENTRE_RECHERCHES_DEMANDEES * 60_000
  if ([v.derniereDemande, v.derniereRecherche].some((d) => d !== null && d.getTime() > limite))
    return 'trop_tot'
  return null
}

// ---------------------------------------------------------------------------------------------
// Passage gardé sur la collecte
// ---------------------------------------------------------------------------------------------

/** Ce que la prise d'une collecte rend à l'agent : le mode figé et la fenêtre (jamais l'entité). */
export type PriseJustificatifs = FenetreJustificatifs & {
  mode: ModeJustificatifs
  motif: MotifModeJustificatifs | null
  /** Version de la règle (`VERSION_REGLE_JUSTIFICATIFS`). */
  version: number
  /**
   * Premier jour de l'exercice examiné : celui qui contient le début de la période décidée
   * (`debutExercice(clôture, decisionDu)`, sinon l'année civile). Les justificatifs sans liaison se
   * comptent à partir de là, l'historique à part. Absent d'un passage figé avant (agent 0.5.1 et
   * serveur du 12/10/2026).
   */
  exerciceDu?: string
}

/** La lecture d'un passage, telle que les lots l'ont rapportée. */
export type LecturePassage = {
  lots: number
  lues: number
  illisibles: number
  complete: boolean
  relecture: RelectureJustificatifs
  documentsSansLiaison: DocumentsSansLiaison | null
}

/**
 * Le passage, gardé sur la collecte (jsonb `justificatifs`, nul pour une collecte de documents) :
 * posé à la création (version, plafond, assistant, mois du passage mensuel), complété à la prise
 * (mode et motif figés, fenêtre), au choix du dossier, à la lecture, au plan, puis aux écritures.
 */
export type PassageJustificatifs = {
  version: number
  plafond: ModeJustificatifs
  viaAssistant: boolean
  /** Mois du passage mensuel (AAAA-MM) ; null pour une demande. */
  mensuel: string | null
  mode?: ModeJustificatifs
  motifMode?: MotifModeJustificatifs | null
  fenetre?: FenetreJustificatifs
  /** Premier jour de l'exercice examiné, figé à la prise (`PriseJustificatifs`). */
  exerciceDu?: string
  /** Dossier retenu chez le portail (référence externe). */
  dossier?: string
  /** Catégorie de dépôt (référence externe), null si introuvable ou ambiguë. */
  categorie?: string | null
  lecture?: LecturePassage
  bilan?: BilanJustificatifs
  /** Les actions rendues à l'agent (`actionsDeVerification`), vingt au plus. */
  plan?: ActionRendueJustificatif[]
  /** Lignes du registre par statut, pour ce passage (étape d'écriture). */
  ecritures?: Partial<Record<StatutJustificatif, number>>
}
